import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X, Building2, Receipt, IndianRupee, Trash2, Pencil, CircleDollarSign, Clock, Wallet, FileDown, AlertTriangle, FileText, Bell, Upload, UserPlus, MessageCircle, MessageSquare, Copy, TrendingUp, Target, Phone, Mail, RefreshCw } from "lucide-react";
import { BACKEND_URL, TOKEN_KEY } from "@/lib/api";
import { CustomerForm } from "./Customers";

// Aging-bucket labels must stay in sync with backend AGING_BUCKETS.
export const AGING_BUCKET_LABELS = ["0-15", "16-30", "31-45", "46-60", "60+"];

/** Pick the aging bucket for an invoice.
 *
 * Buckets are keyed by DAYS SINCE INVOICE DATE (invoice age), NOT by
 * days-past-due-date. So a 41-day-old invoice on 30-day credit sits in
 * "31-45", not "0-15". Non-overdue invoices belong to "current" — this
 * helper is only used for overdue invoices; callers gate on
 * `inv.days_overdue > 0` first.
 */
export function bucketForInvoice(inv) {
  const d = Math.max(0, Number(inv?.days_since_invoice || 0));
  if (d <= 15) return "0-15";
  if (d <= 30) return "16-30";
  if (d <= 45) return "31-45";
  if (d <= 60) return "46-60";
  return "60+";
}

/** Fill placeholders with invoice + party + billing-firm data. */
export function renderTemplate(msg, ctx) {
  const fmt = (n) => (Number(n) || 0).toLocaleString("en-IN");
  const map = {
    customer_name: ctx.customer_name || "Sir/Madam",
    invoice_no: ctx.invoice_no || "",
    amount: fmt(ctx.amount),
    balance: fmt(ctx.balance),
    days_overdue: String(ctx.days_overdue ?? 0),
    due_date: ctx.due_date || "",
    company_name: ctx.company_name || "",
  };
  return String(msg || "").replace(/\{(\w+)\}/g, (_, k) => (k in map ? map[k] : `{${k}}`));
}

async function downloadReport(path, filename) {
  const token = localStorage.getItem(TOKEN_KEY);
  const resp = await fetch(`${BACKEND_URL}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  if (!resp.ok) throw new Error("Download failed");
  const blob = await resp.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}

/**
 * Payments & Collections — manual invoice tracking, per-customer credit terms,
 * partial payments, and a small dashboard tile row on top.
 *
 * Roles:
 *   • admin, account → full CRUD on companies, invoices, payments
 *   • sales          → read invoices + record payments only (no delete/void/edit)
 */
export default function Payments() {
  const { user } = useAuth();
  const canManageInvoices = ["admin", "account"].includes(user?.role);
  const canManageCompanies = ["admin", "account"].includes(user?.role);
  const canRecordPayment = ["admin", "account", "sales"].includes(user?.role);

  const [tab, setTab] = useState("invoices"); // 'invoices' | 'companies' | 'reports'
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [aging, setAging] = useState({ buckets: [], rows: [] });
  const [byParty, setByParty] = useState([]);
  // Aging table sort — click any header to cycle. Default: 60+ high-to-low
  // so the deepest overdue party sits on top.
  const [agingSort, setAgingSort] = useState({ col: "60+", dir: "desc" });
  const [monthly, setMonthly] = useState([]);
  const [statusFilter, setStatusFilter] = useState(""); // '' | unpaid | partial | paid | overdue | void
  const [customerFilter, setCustomerFilter] = useState("");
  const [companyFilter, setCompanyFilter] = useState("");
  const [showInvForm, setShowInvForm] = useState(false);
  const [editInv, setEditInv] = useState(null);
  const [showCoForm, setShowCoForm] = useState(false);
  const [editCo, setEditCo] = useState(null);
  const [payingInv, setPayingInv] = useState(null);
  const [followInv, setFollowInv] = useState(null);
  const [showParty, setShowParty] = useState(false);
  // Standalone advance registration (money received before an invoice is
  // raised — auto-applies to oldest unpaid invoice).
  const [showAdvance, setShowAdvance] = useState(false);
  // Aggregated receipts list — powers the "Receipts" tab.
  const [receipts, setReceipts] = useState([]);
  const [editingReceipt, setEditingReceipt] = useState(null);
  // Party being edited from inside the Payments module (via Party Focus →
  // Edit party). Reuses the same CustomerForm modal as the Customers page.
  const [editParty, setEditParty] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [showTemplates, setShowTemplates] = useState(false);
  const [waPickInv, setWaPickInv] = useState(null);
  const [listModal, setListModal] = useState(null); // {title, tone, invoices}
  const [healthById, setHealthById] = useState({});
  // Bumping this forces PartyFocusPanel to open on the "reminders" tab —
  // used when a temperature chip is clicked from the aging table / Today.
  const [focusInitial, setFocusInitial] = useState({ tab: "summary", tick: 0 });

  // Follow-ups: invoices with a follow_up_date <= today (or no date but has tag)
  const todayIso = new Date().toISOString().slice(0, 10);
  // Invoice grid is party-filtered client-side (dashboard widgets stay whole-firm).
  const gridInvoices = useMemo(
    () => customerFilter ? invoices.filter((i) => i.customer_id === customerFilter) : invoices,
    [invoices, customerFilter],
  );

  const dueFollowUps = invoices.filter((i) =>
    i.status !== "paid" && i.status !== "void" &&
    (i.follow_up_date && i.follow_up_date <= todayIso)
  );

  const load = async () => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (companyFilter) params.company_id = companyFilter;
    // Party filter is applied client-side to the invoice grid only — all
    // dashboard widgets (tiles, aging, follow-ups, schedule, forecast) stay
    // whole-company so the operator can drill into a party without losing
    // firm-level context.
    const reportParams = companyFilter ? { params: { company_id: companyFilter } } : {};
    const [inv, co, cu, ag, bp, mo] = await Promise.all([
      api.get("/invoices", { params }),
      api.get("/billing-companies"),
      api.get("/customers"),
      api.get("/reports/payments/aging", reportParams),
      api.get("/reports/payments/by-party", reportParams),
      api.get("/reports/payments/monthly", reportParams),
    ]);
    setInvoices(inv.data); setCompanies(co.data);
    // Alphabetise once at load so every downstream widget (party filter,
    // typeahead, bulk-import mapper, etc.) shares one canonical order.
    setCustomers([...cu.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "en", { sensitivity: "base" })));
    setAging(ag.data); setByParty(bp.data); setMonthly(mo.data);
    // Best-effort — health is a nice-to-have on the dashboard; failure
    // shouldn't block the primary payment data.
    try {
      const health = await api.get("/customers/party-health", reportParams);
      setHealthById(health.data || {});
    } catch (_) { /* silent */ }
  };
  useEffect(() => { load().catch(() => { /* handled by toast */ }); /* eslint-disable-next-line */ }, [statusFilter, companyFilter]);

  // Click on any temperature chip → jump to that party & open reminders tab.
  const openPartyReminders = (cid) => {
    setCustomerFilter(cid);
    setTab("invoices");
    setFocusInitial({ tab: "reminders", tick: Date.now() });
    // Scroll to the panel after render.
    setTimeout(() => {
      const el = document.querySelector('[data-testid="party-focus"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 250);
  };

  const cust = (id) => customers.find((c) => c.id === id);
  const comp = (id) => companies.find((c) => c.id === id);

  // Aging rows re-sorted according to the current column click. Name column
  // sorts alphabetically (asc / desc). Every numeric column (0-15, 16-30,
  // 31-45, 46-60, 60+, Total) sorts numerically. Toggling the same column
  // flips direction.
  const sortedAgingRows = useMemo(() => {
    const rows = [...(aging.rows || [])];
    const { col, dir } = agingSort;
    const mul = dir === "asc" ? 1 : -1;
    rows.sort((a, b) => {
      if (col === "party") return mul * (a.party || "").localeCompare(b.party || "", "en", { sensitivity: "base" });
      const av = Number(a[col] || 0), bv = Number(b[col] || 0);
      if (av === bv) return (a.party || "").localeCompare(b.party || "");
      return mul * (av - bv);
    });
    return rows;
  }, [aging.rows, agingSort]);

  // Click a header to sort; clicking the same header flips direction.
  const cycleAgingSort = (col) => setAgingSort((s) => ({
    col, dir: s.col === col ? (s.dir === "asc" ? "desc" : "asc") : (col === "party" ? "asc" : "desc"),
  }));

  // Dashboard tiles — computed from currently-loaded invoices
  const totals = useMemo(() => {
    const active = invoices.filter((i) => i.status !== "void");
    const outstanding = active.reduce((s, i) => s + (i.balance || 0), 0);
    const overdue = active.filter((i) => i.days_overdue > 0 && i.balance > 0.01);
    const overdueAmt = overdue.reduce((s, i) => s + i.balance, 0);
    const thisMonth = (() => {
      const now = new Date();
      const ym = now.toISOString().slice(0, 7);
      let sum = 0;
      invoices.forEach((i) => {
        (i.payments_count > 0) && null;
        // we don't have payments list here, so approximate: paid_amount reduced to only invoices dated in this month
        if ((i.invoice_date || "").startsWith(ym)) sum += (i.paid_amount || 0);
      });
      return sum;
    })();
    return {
      outstanding: Math.round(outstanding),
      overdueAmt: Math.round(overdueAmt),
      overdueCount: overdue.length,
      openCount: active.filter((i) => i.status !== "paid").length,
      thisMonth: Math.round(thisMonth),
    };
  }, [invoices]);

  // Per-bucket splits for the aging-bucket tiles + list modals. Only active
  // (non-void) invoices with a positive balance are considered. Each bucket
  // is exclusive so an invoice only appears in one tile.
  const bucketed = useMemo(() => {
    const b = {
      outstanding: [], collected_this_month: [], overdue: [], current: [],
      "0-15": [], "16-30": [], "31-45": [], "46-60": [], "60+": [],
    };
    const ym = new Date().toISOString().slice(0, 7);
    invoices.forEach((i) => {
      if (i.status === "void") return;
      if ((i.paid_amount || 0) > 0 && (i.invoice_date || "").startsWith(ym)) {
        b.collected_this_month.push(i);
      }
      if ((i.balance || 0) > 0.01) {
        b.outstanding.push(i);
        if (i.days_overdue > 0) {
          b.overdue.push(i);
          // Bucket by ACTUAL INVOICE AGE (days_since_invoice), matching
          // the backend aging report + statement — so the tile totals and
          // the aging table on the same screen never contradict.
          b[bucketForInvoice(i)].push(i);
        } else {
          // Not-yet-due invoices — powers the "Not Due" drill-down.
          b.current.push(i);
        }
      }
    });
    return b;
  }, [invoices]);
  const bucketTotal = (arr) => Math.round(arr.reduce((s, i) => s + (i.balance || 0), 0));

  const jumpToFilter = (f) => {
    setTab("invoices");
    setStatusFilter(f);
    setCustomerFilter("");
    setCompanyFilter("");
    setTimeout(() => {
      const el = document.querySelector('[data-testid="invoices-table"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 100);
  };

  const waLink = (inv) => {
    // Kept as a fallback for older callers — the primary flow now opens the
    // WA picker modal so users can choose a template.
    const c = cust(inv.customer_id) || {};
    const phone = (c.phone || "").replace(/[^\d+]/g, "");
    if (!phone) return null;
    return `https://wa.me/${phone.replace(/^\+/, "")}`;
  };

  return (
    <div className="space-y-4" data-testid="payments-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Finance</div>
          <h1 className="text-3xl font-bold tracking-tight text-zinc-950">Payments & Collections</h1>
        </div>
        <div className="flex gap-2 flex-wrap items-stretch">
          {/* Prominent selling-firm selector — every dashboard widget, tile,
             aging table and the Schedule tab honour this scope. It is
             intentionally next to New Invoice so users are certain what
             company any new bill / analysis belongs to. */}
          <label className="flex items-stretch border-2 border-orange-500 bg-white overflow-hidden" data-testid="company-scope-wrap">
            <span className="bg-orange-50 text-orange-700 px-3 flex items-center text-[10px] font-mono uppercase tracking-widest border-r-2 border-orange-500">
              <Building2 size={14} className="mr-1.5" /> Firm
            </span>
            <select data-testid="company-scope" value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
              className="text-sm px-2 outline-none font-semibold text-zinc-900 min-w-[180px] bg-white">
              <option value="">All Firms</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
          {canManageInvoices && (
            <button data-testid="digest-preview-btn"
              onClick={() => {
                if (!companyFilter) return toast.error("Pick a Firm above to preview its digest");
                const token = (localStorage.getItem("stretchops_token") || "").trim();
                // Preview endpoint returns raw HTML — open a fresh window and
                // stream it in via fetch so the auth header can be attached.
                const win = window.open("", "_blank");
                if (!win) return toast.error("Popup blocked");
                win.document.write('<div style="font-family:monospace;padding:24px;">Loading preview…</div>');
                fetch(`${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api/reports/weekly-digest/preview?company_id=${encodeURIComponent(companyFilter)}`, {
                  headers: { Authorization: `Bearer ${token}` },
                }).then((r) => r.ok ? r.text() : Promise.reject(r.statusText))
                  .then((html) => { win.document.open(); win.document.write(html); win.document.close(); })
                  .catch((err) => { win.document.body.innerText = `Preview failed: ${err}`; toast.error(String(err)); });
              }}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <Mail size={14} /> Preview digest
            </button>
          )}
          {canManageInvoices && (
            <button data-testid="new-advance-btn"
              onClick={() => setShowAdvance(true)}
              title="Register a receipt that isn't tied to any invoice — will auto-apply to oldest unpaid invoice"
              className="border border-emerald-500 hover:bg-emerald-50 text-emerald-700 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <CircleDollarSign size={14} /> + Advance
            </button>
          )}
          {canManageInvoices && (
            <button data-testid="new-party-btn" onClick={() => setShowParty(true)}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <UserPlus size={14} /> New Party
            </button>
          )}
          {canManageInvoices && (
            <button data-testid="new-note-btn"
              onClick={async () => {
                const type = window.prompt("Note type — credit (return/discount) or debit (extra charge)?", "credit");
                if (!type || !["credit", "debit"].includes(type)) return;
                const note_no = window.prompt("Note number?"); if (!note_no) return;
                const cid = window.prompt("Customer id (see Customers page)"); if (!cid) return;
                const coid = window.prompt("Billing firm id"); if (!coid) return;
                const amt = parseFloat(window.prompt("Amount ₹") || "0"); if (!amt) return;
                const date = window.prompt("Date (YYYY-MM-DD)", new Date().toISOString().slice(0,10)); if (!date) return;
                const reason = window.prompt("Reason?", "") || "";
                try {
                  await api.post("/notes", { note_no, note_type: type, company_id: coid, customer_id: cid, date, amount: amt, reason });
                  toast.success("Note saved");
                } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
              }}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <FileText size={14} /> Note
            </button>
          )}
          {canManageInvoices && (
            <button data-testid="new-pdc-btn"
              onClick={async () => {
                const cheque_no = window.prompt("Cheque number?"); if (!cheque_no) return;
                const cid = window.prompt("Customer id"); if (!cid) return;
                const bank = window.prompt("Bank?"); if (!bank) return;
                const maturity_date = window.prompt("Maturity date (YYYY-MM-DD)"); if (!maturity_date) return;
                const amount = parseFloat(window.prompt("Amount ₹") || "0"); if (!amount) return;
                try {
                  await api.post("/pdc", { cheque_no, customer_id: cid, bank, maturity_date, amount });
                  toast.success("PDC saved");
                } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
              }}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <Wallet size={14} /> PDC
            </button>
          )}
          {canManageInvoices && (
            <button data-testid="reminder-templates-btn" onClick={() => setShowTemplates(true)}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <MessageSquare size={14} /> Templates
            </button>
          )}
          {canManageInvoices && tab === "invoices" && (
            <button data-testid="import-invoices-btn" onClick={() => setShowImport(true)}
              className="border border-zinc-400 hover:bg-zinc-100 text-zinc-800 px-3 py-2 text-xs uppercase tracking-wider font-mono flex items-center gap-2">
              <Upload size={14} /> Import XLS
            </button>
          )}
          {tab === "invoices" && canManageInvoices && (
            <button data-testid="new-invoice-btn" onClick={() => { setEditInv(null); setShowInvForm(true); }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2">
              <Plus size={16} /> New Invoice
            </button>
          )}
          {tab === "companies" && canManageCompanies && (
            <button data-testid="new-company-btn" onClick={() => { setEditCo(null); setShowCoForm(true); }}
              className="bg-orange-600 hover:bg-orange-700 text-white px-4 py-2 text-sm uppercase tracking-wider font-medium flex items-center gap-2">
              <Plus size={16} /> New Company
            </button>
          )}
        </div>
      </div>

      {/* Dashboard tiles */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard testid="tile-outstanding" onClick={() => setListModal({ title: "All Outstanding Invoices", tone: "orange", invoices: bucketed.outstanding })} icon={<Wallet size={16} />} label="Outstanding — click for list" value={`₹ ${totals.outstanding.toLocaleString("en-IN")}`} accent="orange" note={`${totals.openCount} open`} />
        <StatCard testid="tile-overdue" onClick={() => setListModal({ title: "All Overdue Invoices", tone: "red", invoices: bucketed.overdue })} icon={<Clock size={16} />} label="Overdue — click for list" value={`₹ ${totals.overdueAmt.toLocaleString("en-IN")}`} accent="red" note={`${totals.overdueCount} invoices`} />
        <StatCard testid="tile-this-month" onClick={() => setListModal({ title: "Collected This Month", tone: "emerald", invoices: bucketed.collected_this_month })} icon={<CircleDollarSign size={16} />} label="Collected — click for list" value={`₹ ${totals.thisMonth.toLocaleString("en-IN")}`} accent="emerald" note="this month" />
        <StatCard testid="tile-companies" icon={<Building2 size={16} />} label="Selling Firms" value={String(companies.length)} accent="zinc" note={`${customers.length} parties`} />
      </div>

      {/* Aging-bucket tiles — click to open just that bucket in a modal */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          { k: "0-15", label: "0-15 days", tone: "yellow" },
          { k: "16-30", label: "16-30 days", tone: "amber" },
          { k: "31-45", label: "31-45 days", tone: "orange" },
          { k: "46-60", label: "46-60 days", tone: "red" },
          { k: "60+", label: "60+ days", tone: "red" },
        ].map((b) => {
          const list = bucketed[b.k];
          return (
            <StatCard
              key={b.k}
              testid={`tile-bucket-${b.k}`}
              onClick={() => setListModal({ title: `Overdue ${b.label}`, tone: b.tone, invoices: list })}
              icon={<Clock size={16} />}
              label={`${b.label} — click for list`}
              value={`₹ ${bucketTotal(list).toLocaleString("en-IN")}`}
              accent={b.tone}
              note={`${list.length} invoice${list.length === 1 ? "" : "s"}`}
            />
          );
        })}
      </div>

      {/* Follow-ups Due — alert bar shown only when there are matches */}
      {dueFollowUps.length > 0 && (
        <div className="bg-yellow-50 border-2 border-yellow-500 p-3" data-testid="follow-ups-due">
          <div className="flex items-center gap-2 mb-2">
            <Bell size={14} className="text-yellow-700" />
            <div className="text-xs font-mono uppercase tracking-widest text-yellow-800 font-semibold">
              {dueFollowUps.length} follow-up{dueFollowUps.length === 1 ? "" : "s"} due today
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {dueFollowUps.slice(0, 6).map((i) => {
              const c = cust(i.customer_id);
              return (
                <div key={i.id} className="bg-white border border-yellow-300 px-3 py-2 text-xs flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-zinc-900 truncate">{i.invoice_no} · {c?.name}</div>
                    <div className="text-[10px] font-mono text-yellow-800 truncate">📌 {i.follow_up || "Follow up"} · {i.follow_up_date}</div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {c?.phone && (
                      <button data-testid={`due-wa-${i.id}`} type="button" onClick={() => setWaPickInv(i)}
                        className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-1 inline-flex items-center gap-1">
                        <MessageCircle size={10} /> WA
                      </button>
                    )}
                    <button data-testid={`open-followup-${i.id}`} onClick={() => setFollowInv(i)}
                      className="text-[10px] font-mono uppercase border border-yellow-600 text-yellow-800 hover:bg-yellow-100 px-2 py-1">Open</button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Aging + Top 5 overdue side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 bg-white border border-zinc-300 flex flex-col" data-testid="aging-table">
          <div className="px-4 py-2 border-b border-zinc-200 flex items-center justify-between bg-zinc-50 shrink-0">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 flex items-center gap-1.5">
              <Clock size={11} /> Aging — Outstanding by Days
              <span className="text-zinc-400">· {aging.rows.length} part{aging.rows.length === 1 ? "y" : "ies"}</span>
            </div>
            <div className="flex gap-1">
              <button data-testid="aging-pdf" onClick={() => downloadReport("/api/reports/payments/aging.pdf", "aging.pdf").catch(() => toast.error("Download failed"))}
                className="text-[10px] uppercase font-mono border border-zinc-300 px-2 py-0.5 hover:bg-zinc-100 flex items-center gap-1"><FileDown size={10} /> PDF</button>
              <button data-testid="aging-xlsx" onClick={() => downloadReport("/api/reports/payments/aging.xlsx", "aging.xlsx").catch(() => toast.error("Download failed"))}
                className="text-[10px] uppercase font-mono border border-zinc-300 px-2 py-0.5 hover:bg-zinc-100 flex items-center gap-1"><FileDown size={10} /> XLS</button>
            </div>
          </div>
          {/* Scrollable body — every party shown, header + totals stay pinned so the
              layout height doesn't drift with data volume. */}
          <div className="overflow-auto max-h-[380px]">
            <table className="w-full text-xs font-mono">
              <thead className="bg-zinc-100 text-zinc-600 uppercase text-[9px] tracking-wider sticky top-0 z-10">
                <tr>
                  <th className="text-left px-3 py-1.5 border-b bg-zinc-100">
                    <button type="button" onClick={() => cycleAgingSort("party")}
                      data-testid="aging-sort-party"
                      className="inline-flex items-center gap-1 hover:text-orange-700">
                      Party {agingSort.col === "party" ? (agingSort.dir === "asc" ? "▲" : "▼") : "⇅"}
                    </button>
                  </th>
                  {aging.buckets.map((b) => (
                    <th key={b} className="text-right px-2 py-1.5 border-b bg-zinc-100">
                      <button type="button" onClick={() => cycleAgingSort(b)}
                        data-testid={`aging-sort-${b}`}
                        className="inline-flex items-center gap-1 hover:text-orange-700">
                        {b === "current" ? "Not Due" : b} {agingSort.col === b ? (agingSort.dir === "asc" ? "▲" : "▼") : "⇅"}
                      </button>
                    </th>
                  ))}
                  <th className="text-right px-3 py-1.5 border-b bg-zinc-100">
                    <button type="button" onClick={() => cycleAgingSort("total")}
                      data-testid="aging-sort-total"
                      className="inline-flex items-center gap-1 hover:text-orange-700">
                      Total {agingSort.col === "total" ? (agingSort.dir === "asc" ? "▲" : "▼") : "⇅"}
                    </button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedAgingRows.length === 0 ? (
                  <tr><td colSpan={aging.buckets.length + 2} className="text-center text-zinc-500 py-6 font-sans">No outstanding invoices.</td></tr>
                ) : sortedAgingRows.map((r) => (
                  <tr key={r.customer_id} className="border-b border-zinc-100 hover:bg-zinc-50">
                    <td className="px-3 py-1.5 font-sans">
                      <div className="flex items-center gap-2">
                        <button type="button" onClick={() => setListModal({ title: `Outstanding — ${r.party}`, tone: "orange",
                          invoices: bucketed.outstanding.filter((i) => i.customer_id === r.customer_id) })}
                          className="text-zinc-900 hover:text-orange-700 hover:underline text-left"
                          data-testid={`aging-row-${r.customer_id}`}>{r.party}</button>
                        <PartyHealthChip
                          health={healthById[r.customer_id]}
                          size="sm"
                          onClick={() => openPartyReminders(r.customer_id)}
                          testid={`aging-health-${r.customer_id}`}
                        />
                      </div>
                    </td>
                    {aging.buckets.map((b) => (
                      <td key={b} className={`text-right px-2 py-1.5 ${r[b] > 0 ? (b === "60+" ? "text-red-700 font-semibold" : b === "46-60" ? "text-orange-700" : "text-zinc-800") : "text-zinc-300"}`}>
                        {r[b] > 0 ? (
                          <button type="button" data-testid={`aging-cell-${r.customer_id}-${b}`}
                            onClick={() => setListModal({ title: `${r.party} · ${b === "current" ? "Not Due" : `${b} days`}`, tone: b === "60+" ? "red" : b === "46-60" ? "red" : (b === "current" ? "emerald" : "orange"),
                              invoices: (bucketed[b] || []).filter((i) => i.customer_id === r.customer_id) })}
                            className="hover:underline">{r[b].toLocaleString("en-IN")}</button>
                        ) : "—"}
                      </td>
                    ))}
                    <td className="text-right px-3 py-1.5 font-semibold text-zinc-900">{r.total.toLocaleString("en-IN")}</td>
                  </tr>
                ))}
              </tbody>
              {aging.rows.length > 0 && (
                <tfoot className="sticky bottom-0 z-10">
                  <tr className="bg-zinc-50 font-semibold border-t border-zinc-300">
                    <td className="px-3 py-1.5 bg-zinc-50">TOTAL</td>
                    {aging.buckets.map((b) => (
                      <td key={b} className="text-right px-2 py-1.5 bg-zinc-50">
                        {aging.rows.reduce((s, r) => s + (r[b] || 0), 0).toLocaleString("en-IN")}
                      </td>
                    ))}
                    <td className="text-right px-3 py-1.5 bg-zinc-50">
                      {aging.rows.reduce((s, r) => s + (r.total || 0), 0).toLocaleString("en-IN")}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>

        <div className="bg-white border border-zinc-300" data-testid="top-overdue-tile">
          <div className="px-4 py-2 border-b border-zinc-200 bg-red-50 text-[10px] font-mono uppercase tracking-widest text-red-800 flex items-center gap-1.5">
            <AlertTriangle size={11} /> Top 5 Overdue Parties
          </div>
          <div className="divide-y divide-zinc-100">
            {(byParty.filter((p) => p.overdue > 0).slice(0, 5)).length === 0 ? (
              <div className="text-center text-zinc-500 py-6 text-xs font-mono">No overdue parties. 🎉</div>
            ) : byParty.filter((p) => p.overdue > 0).slice(0, 5).map((p, idx) => (
              <div key={p.customer_id} className="px-3 py-2 flex items-center gap-2 hover:bg-red-50">
                <div className="text-[10px] font-mono text-zinc-400 w-4">{idx + 1}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-zinc-900 truncate">{p.party}</div>
                  <div className="text-[10px] font-mono text-zinc-500 truncate">{p.company || "—"}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono font-bold text-red-700">₹ {p.overdue.toLocaleString("en-IN")}</div>
                  <div className="text-[9px] font-mono text-zinc-500">{p.invoices} inv</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-zinc-300">
        {[
          { k: "invoices",  label: "Invoices",  Icon: Receipt },
          { k: "receipts",  label: "Receipts",  Icon: IndianRupee },
          { k: "openings",  label: "Openings",  Icon: CircleDollarSign },
          { k: "today",     label: "Today's Task", Icon: Target },
          { k: "schedule",  label: "Schedule",  Icon: Clock },
          { k: "forecast",  label: "Forecast",  Icon: TrendingUp },
          { k: "companies", label: "My Companies", Icon: Building2 },
          { k: "reports",   label: "Reports",   Icon: FileDown },
        ].map(({ k, label, Icon }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-xs uppercase tracking-wider font-mono border-b-2 -mb-px flex items-center gap-1 ${
              tab === k ? "border-orange-600 text-zinc-950" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}>
            <Icon size={14} /> {label}
          </button>
        ))}
      </div>

      {tab === "invoices" && (
        <>
          <div className="bg-white border border-zinc-300 p-3 flex flex-wrap gap-2 items-center">
            <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">Filter</span>
            {["", "unpaid", "partial", "paid", "overdue", "void"].map((s) => (
              <button key={s || "all"} data-testid={`filter-${s || "all"}`}
                onClick={() => setStatusFilter(s)}
                className={`text-[10px] uppercase font-mono px-2.5 py-1 border ${statusFilter === s ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}
              >{s || "ALL"}</button>
            ))}
            <PartyPicker customers={customers} value={customerFilter} onChange={setCustomerFilter} />
          </div>

          <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="invoices-table">
            <table className="w-full text-sm border-collapse">
              <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2 border-b">Invoice #</th>
                  <th className="text-left px-3 py-2 border-b">Party</th>
                  <th className="text-left px-3 py-2 border-b">Billing Firm</th>
                  <th className="text-left px-3 py-2 border-b">Date</th>
                  <th className="text-left px-3 py-2 border-b">Due</th>
                  <th className="text-right px-3 py-2 border-b">Gross ₹</th>
                  <th className="text-right px-3 py-2 border-b">Paid ₹</th>
                  <th className="text-right px-3 py-2 border-b">Balance ₹</th>
                  <th className="text-center px-3 py-2 border-b">Status</th>
                  <th className="text-left px-3 py-2 border-b">Action</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {gridInvoices.length === 0 ? (
                  <tr><td colSpan={10} className="text-center text-zinc-500 py-12 text-sm font-sans">
                    {customerFilter ? "This party has no invoices on this firm yet." : "No invoices yet."}
                  </td></tr>
                ) : gridInvoices.map((i) => {
                  const c = cust(i.customer_id); const co = comp(i.company_id);
                  const overdue = i.days_overdue > 0 && i.balance > 0.01;
                  return (
                    <tr key={i.id} className="border-b border-zinc-200 hover:bg-zinc-50" data-testid={`invoice-row-${i.id}`}>
                      <td className="px-3 py-2 font-semibold text-zinc-900">{i.invoice_no}</td>
                      <td className="px-3 py-2 font-sans">{c?.name || "—"}{c?.company ? <div className="text-[10px] text-zinc-500">{c.company}</div> : null}</td>
                      <td className="px-3 py-2 text-zinc-700">{co?.name || "—"}</td>
                      <td className="px-3 py-2">{i.invoice_date}</td>
                      <td className={`px-3 py-2 ${overdue ? "text-red-700 font-semibold" : "text-zinc-700"}`}>
                        {i.due_date}
                        {overdue && <div className="text-[10px]">↑ {i.days_overdue}d over</div>}
                      </td>
                      <td className="px-3 py-2 text-right">{i.gross_amount.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{i.paid_amount.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-right font-semibold text-zinc-900">{i.balance.toLocaleString("en-IN")}</td>
                      <td className="px-3 py-2 text-center">
                        <span className={`text-[9px] uppercase border px-2 py-0.5 ${
                          i.status === "paid" ? "bg-emerald-50 text-emerald-700 border-emerald-600" :
                          overdue ? "bg-red-50 text-red-700 border-red-500" :
                          i.status === "partial" ? "bg-blue-50 text-blue-700 border-blue-500" :
                          i.status === "void" ? "bg-zinc-100 text-zinc-500 border-zinc-300 line-through" :
                          "bg-orange-50 text-orange-700 border-orange-500"
                        }`}>{overdue ? "OVERDUE" : i.status}</span>
                      </td>
                      <td className="px-3 py-2 flex gap-1 flex-wrap">
                        <a data-testid={`statement-${i.id}`} href={`/customers/${i.customer_id}/statement?firm=${encodeURIComponent(i.company_id || "")}`} target="_blank" rel="noreferrer"
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-400 text-zinc-800 hover:bg-zinc-100 px-2 py-1"
                          title="Open customer statement (firm-scoped) in a new tab">
                          <FileText size={10} /> Stmt
                        </a>
                        <button data-testid={`follow-up-${i.id}`} onClick={() => setFollowInv(i)}
                          className={`inline-flex items-center gap-1 text-[10px] uppercase font-mono border px-2 py-1 ${i.follow_up ? "border-yellow-600 bg-yellow-50 text-yellow-800 hover:bg-yellow-100" : "border-zinc-400 text-zinc-800 hover:bg-zinc-100"}`}
                          title={i.follow_up ? `${i.follow_up}${i.follow_up_date ? " · " + i.follow_up_date : ""}` : "Set follow-up"}>
                          <Bell size={10} /> {i.follow_up ? "📌" : "Tag"}
                        </button>
                        {i.balance > 0.01 && i.status !== "void" && (() => {
                          const hasPhone = !!(cust(i.customer_id)?.phone || "").trim();
                          return hasPhone ? (
                            <button data-testid={`wa-${i.id}`} type="button" onClick={() => setWaPickInv(i)}
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-1"
                              title="Pick a WhatsApp reminder template and send">
                              <MessageCircle size={10} /> WA
                            </button>
                          ) : (
                            <button data-testid={`wa-missing-${i.id}`} type="button" disabled
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-300 text-zinc-400 px-2 py-1 cursor-not-allowed"
                              title="Add a phone number on the party master to enable WhatsApp reminders">
                              <MessageCircle size={10} /> WA
                            </button>
                          );
                        })()}
                        {canRecordPayment && i.balance > 0.01 && i.status !== "void" && (
                          <button data-testid={`record-payment-${i.id}`} onClick={() => setPayingInv(i)}
                            className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-emerald-600 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-2 py-1">
                            <IndianRupee size={10} /> Record
                          </button>
                        )}
                        {canManageInvoices && (
                          <>
                            <button data-testid={`edit-invoice-${i.id}`} onClick={() => { setEditInv(i); setShowInvForm(true); }}
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-400 hover:bg-zinc-100 px-2 py-1">
                              <Pencil size={10} /> Edit
                            </button>
                            <button data-testid={`delete-invoice-${i.id}`}
                              onClick={async () => { if (!window.confirm(`Delete ${i.invoice_no}? All payments will be removed.`)) return;
                                try { await api.delete(`/invoices/${i.id}`); toast.success("Invoice deleted"); load(); }
                                catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } }}
                              className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-50 px-2 py-1">
                              <Trash2 size={10} />
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {customerFilter && (
            <PartyFocusPanel
              customer={cust(customerFilter) || {}}
              invoices={invoices.filter((i) => i.customer_id === customerFilter)}
              cust={cust}
              health={healthById[customerFilter]}
              initialTab={focusInitial.tab}
              initialTabTick={focusInitial.tick}
              onOpenInvoice={(inv) => setPayingInv(inv)}
              onFollowUp={(inv) => setFollowInv(inv)}
              onWa={(inv) => setWaPickInv(inv)}
              onEditParty={(c) => setEditParty(c)}
              onReloadDashboard={load}
              companyFilter={companyFilter}
            />
          )}
        </>
      )}

      {tab === "schedule" && (
        <ScheduleTab
          invoices={invoices}
          companyFilter={companyFilter}
          cust={cust}
          onOpenInvoice={(inv) => setPayingInv(inv)}
          onFollowUp={(inv) => setFollowInv(inv)}
          onWa={(inv) => setWaPickInv(inv)}
        />
      )}

      {tab === "forecast" && (
        <ForecastTab companyFilter={companyFilter} />
      )}

      {tab === "receipts" && (
        <ReceiptsTab
          companyFilter={companyFilter}
          receipts={receipts}
          setReceipts={setReceipts}
          onEdit={(r) => setEditingReceipt(r)}
          onOpenParty={(cid) => { setCustomerFilter(cid); setTab("invoices"); }}
        />
      )}
      {tab === "openings" && (
        <OpeningsTab
          companies={companies}
          customers={customers}
          companyFilter={companyFilter}
          setCompanyFilter={setCompanyFilter}
          onSaved={load}
        />
      )}
      {tab === "today" && (
        <TodayTab
          companyFilter={companyFilter}
          cust={cust}
          invoices={invoices}
          healthById={healthById}
          onOpenParty={openPartyReminders}
          onWa={(inv) => setWaPickInv(inv)}
          onRecord={(inv) => setPayingInv(inv)}
          onOpenStatement={(cid) => window.open(`/customers/${cid}/statement${companyFilter ? `?firm=${encodeURIComponent(companyFilter)}` : ""}`, "_blank", "noreferrer")}
        />
      )}

      {tab === "companies" && (
        <div className="bg-white border border-zinc-300 overflow-x-auto" data-testid="companies-table">
          <table className="w-full text-sm border-collapse">
            <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] font-mono tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 border-b">Name</th>
                <th className="text-left px-3 py-2 border-b">GST</th>
                <th className="text-left px-3 py-2 border-b">PAN</th>
                <th className="text-left px-3 py-2 border-b">Bank</th>
                <th className="text-left px-3 py-2 border-b">Contact</th>
                <th className="text-left px-3 py-2 border-b">Action</th>
              </tr>
            </thead>
            <tbody className="font-mono">
              {companies.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-zinc-500 py-12 text-sm font-sans">No companies yet. Add your billing firms.</td></tr>
              ) : companies.map((c) => (
                <tr key={c.id} className="border-b border-zinc-200 hover:bg-zinc-50">
                  <td className="px-3 py-2 font-semibold text-zinc-900">{c.name}</td>
                  <td className="px-3 py-2">{c.gst || "—"}</td>
                  <td className="px-3 py-2">{c.pan || "—"}</td>
                  <td className="px-3 py-2">{c.bank_name ? `${c.bank_name} · ${c.bank_account || ""}` : "—"}</td>
                  <td className="px-3 py-2 font-sans text-xs">{c.phone || ""} {c.email ? <div className="text-zinc-500">{c.email}</div> : null}</td>
                  <td className="px-3 py-2 flex gap-1">
                    {canManageCompanies && (
                      <>
                        <button data-testid={`edit-co-${c.id}`} onClick={() => { setEditCo(c); setShowCoForm(true); }}
                          className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-zinc-400 hover:bg-zinc-100 px-2 py-1">
                          <Pencil size={10} /> Edit
                        </button>
                        {user?.role === "admin" && (
                          <button data-testid={`del-co-${c.id}`}
                            onClick={async () => { if (!window.confirm(`Delete company ${c.name}?`)) return;
                              try { await api.delete(`/billing-companies/${c.id}`); toast.success("Deleted"); load(); }
                              catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } }}
                            className="inline-flex items-center gap-1 text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-50 px-2 py-1">
                            <Trash2 size={10} />
                          </button>
                        )}
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {tab === "reports" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="reports-grid">
          {[
            { key: "monthly",  title: "Monthly Collection", desc: "Payments received grouped by month + mode of payment (cash / bank / UPI / cheque)", rows: monthly.length, unit: "months" },
            { key: "by-party", title: "Collection — By Party", desc: "Per-customer invoiced, paid, balance and overdue totals", rows: byParty.length, unit: "parties" },
            { key: "overdue",  title: "Overdue Invoices", desc: "Every invoice past its due date with days-over-due count", rows: totals.overdueCount, unit: "invoices" },
            { key: "aging",    title: "Aging Report", desc: `Outstanding split into ${aging.buckets.join(" · ")} day buckets`, rows: aging.rows.length, unit: "parties" },
          ].map((r) => (
            <div key={r.key} className="bg-white border border-zinc-300 p-4" data-testid={`report-card-${r.key}`}>
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{r.rows} {r.unit}</div>
              <div className="text-lg font-semibold text-zinc-950">{r.title}</div>
              <div className="text-xs text-zinc-600 mt-1 mb-3">{r.desc}</div>
              <div className="flex gap-2">
                <button data-testid={`dl-${r.key}-pdf`}
                  onClick={() => downloadReport(`/api/reports/payments/${r.key}.pdf`, `${r.key}.pdf`).catch(() => toast.error("PDF failed"))}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-zinc-400 text-zinc-800 hover:bg-zinc-100 px-3 py-2 text-[11px] uppercase font-mono">
                  <FileDown size={12} /> Download PDF
                </button>
                <button data-testid={`dl-${r.key}-xlsx`}
                  onClick={() => downloadReport(`/api/reports/payments/${r.key}.xlsx`, `${r.key}.xlsx`).catch(() => toast.error("XLS failed"))}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 border border-emerald-600 text-emerald-800 bg-emerald-50 hover:bg-emerald-100 px-3 py-2 text-[11px] uppercase font-mono">
                  <FileDown size={12} /> Download XLS
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {(showInvForm) && (
        <InvoiceForm initial={editInv} companies={companies} customers={customers}
          onClose={() => { setShowInvForm(false); setEditInv(null); }}
          onSaved={() => { setShowInvForm(false); setEditInv(null); load(); }} />
      )}
      {(showCoForm) && (
        <CompanyForm initial={editCo}
          onClose={() => { setShowCoForm(false); setEditCo(null); }}
          onSaved={() => { setShowCoForm(false); setEditCo(null); load(); }} />
      )}
      {payingInv && (
        <PaymentModal invoice={payingInv} onClose={() => setPayingInv(null)} onDone={() => { setPayingInv(null); load(); }} />
      )}
      {followInv && (
        <FollowUpModal invoice={followInv} onClose={() => setFollowInv(null)} onDone={() => { setFollowInv(null); load(); }} />
      )}
      {showParty && (
        <PartyModal onClose={() => setShowParty(false)} onDone={() => { setShowParty(false); load(); }} />
      )}
      {showAdvance && (
        <AdvanceRegisterModal customers={customers} companies={companies} companyFilter={companyFilter}
          onClose={() => setShowAdvance(false)}
          onSaved={() => { setShowAdvance(false); load(); toast.success("Advance registered"); }} />
      )}
      {editingReceipt && (
        <ReceiptEditModal receipt={editingReceipt}
          onClose={() => setEditingReceipt(null)}
          onSaved={() => { setEditingReceipt(null); load(); }} />
      )}
      {editParty && (
        <CustomerForm editing={editParty} onClose={() => setEditParty(null)}
          onSaved={() => { setEditParty(null); load(); }} />
      )}
      {showImport && (
        <ImportInvoicesModal onClose={() => setShowImport(false)} onDone={() => { setShowImport(false); load(); }} />
      )}
      {showTemplates && (
        <ReminderTemplatesModal onClose={() => setShowTemplates(false)} />
      )}
      {waPickInv && (
        <WaPickerModal
          invoice={waPickInv}
          customer={cust(waPickInv.customer_id) || {}}
          company={comp(waPickInv.company_id) || {}}
          onClose={() => setWaPickInv(null)}
        />
      )}
      {listModal && (
        <InvoiceListModal
          title={listModal.title}
          tone={listModal.tone}
          invoices={listModal.invoices}
          cust={cust}
          comp={comp}
          onOpenStatement={(id, coId) => window.open(`/customers/${id}/statement${(coId || companyFilter) ? `?firm=${encodeURIComponent(coId || companyFilter)}` : ""}`, "_blank", "noreferrer")}
          onRecord={(inv) => { setListModal(null); setPayingInv(inv); }}
          onEdit={(inv) => { setListModal(null); setEditInv(inv); setShowInvForm(true); }}
          onWa={(inv) => { setListModal(null); setWaPickInv(inv); }}
          onClose={() => setListModal(null)}
        />
      )}
    </div>
  );
}

function PartyModal({ onClose, onDone }) {
  const [s, setS] = useState({ name: "", company: "", phone: "", email: "", gst: "", address: "", default_credit_days: 30 });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!s.name.trim()) return toast.error("Party name required");
    setBusy(true);
    try {
      await api.post("/customers", s);
      toast.success(`Party ${s.name} added — available across the app`);
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg p-6 space-y-3" data-testid="party-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Add Party</div>
            <h2 className="text-lg font-bold tracking-tight">Customer / Party</h2>
            <div className="text-[10px] font-mono text-zinc-500 mt-1">Same master used by Orders, Inquiries, Delivery Notes.</div>
          </div>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Name *"><input data-testid="party-name" required value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} className={cls} /></Field>
          <Field label="Company"><input data-testid="party-company" value={s.company} onChange={(e) => setS({ ...s, company: e.target.value })} className={cls} /></Field>
          <Field label="Phone"><input data-testid="party-phone" value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} className={cls} /></Field>
          <Field label="Email"><input value={s.email} type="email" onChange={(e) => setS({ ...s, email: e.target.value })} className={cls} /></Field>
          <Field label="GST"><input value={s.gst} onChange={(e) => setS({ ...s, gst: e.target.value })} className={cls} /></Field>
          <Field label="Default Credit Days">
            <input data-testid="party-credit-days" type="number" min="0" max="365" value={s.default_credit_days} onChange={(e) => setS({ ...s, default_credit_days: Number(e.target.value) || 0 })} className={cls} />
          </Field>
          <div className="col-span-2">
            <Field label="Address"><textarea rows={2} value={s.address} onChange={(e) => setS({ ...s, address: e.target.value })} className={cls} /></Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={onClose} className="px-4 py-1.5 border border-zinc-300 text-xs uppercase font-mono">Cancel</button>
          <button type="submit" disabled={busy} data-testid="party-save"
            className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase font-mono disabled:opacity-50">
            {busy ? "Saving…" : "Add Party"}
          </button>
        </div>
      </form>
    </div>
  );
}

function ImportInvoicesModal({ onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const downloadTpl = async () => {
    try {
      const { BACKEND_URL: bu, TOKEN_KEY: tk } = await import("@/lib/api");
      const token = localStorage.getItem(tk);
      const r = await fetch(`${bu}/api/invoices/import-template.xlsx`, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = "invoice-import-template.xlsx"; a.click();
    } catch { toast.error("Template download failed"); }
  };
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pick an .xlsx file first");
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/invoices/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      if (data.created > 0) toast.success(`${data.created} invoice(s) imported`);
      if ((data.auto_created_parties?.length || 0) > 0) toast.info(`${data.auto_created_parties.length} new part${data.auto_created_parties.length === 1 ? "y" : "ies"} auto-created`);
      if (data.failed > 0) toast.warning(`${data.failed} row(s) skipped — see report below`);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl p-6 space-y-3" data-testid="import-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Bulk Import</div>
            <h2 className="text-lg font-bold tracking-tight">Import invoices from Excel</h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="bg-zinc-50 border border-zinc-200 p-3 text-[11px] font-mono text-zinc-700 space-y-1">
          <div><b>Steps:</b></div>
          <div>1. Download the template · fill one row per invoice</div>
          <div>2. Missing customers are auto-created; billing firms must already exist</div>
          <div>3. Duplicate invoice numbers and bad rows are skipped &amp; reported below</div>
          <button type="button" onClick={downloadTpl} data-testid="import-tpl-btn"
            className="mt-2 inline-flex items-center gap-1 border border-zinc-400 hover:bg-zinc-100 px-2 py-1 text-[10px] uppercase">
            <FileDown size={10} /> Download template
          </button>
        </div>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">.xlsx file</div>
          <input data-testid="import-file" type="file" accept=".xlsx" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="w-full text-sm border border-zinc-300 px-2 py-1.5" />
        </label>
        {result && (
          <div className="border border-zinc-300 p-2 text-xs max-h-56 overflow-y-auto" data-testid="import-result">
            <div className="font-semibold mb-1 flex items-center justify-between gap-2 flex-wrap">
              <span><span className="text-emerald-700">{result.created} created</span> · <span className="text-red-700">{result.failed} skipped</span>{(result.auto_created_parties?.length || 0) > 0 && (
                <span className="text-orange-700"> · {result.auto_created_parties.length} new part{result.auto_created_parties.length === 1 ? "y" : "ies"} added</span>
              )}</span>
              {result.failed_rows.length > 0 && (
                <div className="flex items-center gap-1.5">
                  <button type="button" data-testid="import-error-xlsx"
                    onClick={async () => {
                      try {
                        const { BACKEND_URL: bu, TOKEN_KEY: tk } = await import("@/lib/api");
                        const token = localStorage.getItem(tk);
                        const r = await fetch(`${bu}/api/invoices/bulk-import/error-report.xlsx`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
                          body: JSON.stringify({ failed_rows: result.failed_rows }),
                        });
                        if (!r.ok) throw new Error("failed");
                        const blob = await r.blob();
                        const a = document.createElement("a");
                        a.href = URL.createObjectURL(blob);
                        a.download = `invoice-import-errors-${new Date().toISOString().slice(0,10)}.xlsx`;
                        a.click();
                      } catch { toast.error("Error report download failed"); }
                    }}
                    className="text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-50 px-2 py-0.5 flex items-center gap-1">
                    <FileDown size={10} /> Error XLSX
                  </button>
                  <button type="button" data-testid="import-error-csv"
                    onClick={() => {
                      const rows = [["row", "invoice_no", "invoice_date", "customer", "billing_company", "credit_days", "gross_amount", "notes", "reason"],
                        ...result.failed_rows.map((r) => [r.row, r.invoice_no || "", r.invoice_date || "", r.customer_name_or_id || "", r.billing_company_name_or_id || "", r.credit_days || "", r.gross_amount || "", r.notes || "", r.reason])];
                      const csv = rows.map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(",")).join("\n");
                      const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
                      const a = document.createElement("a");
                      a.href = URL.createObjectURL(blob);
                      a.download = `import-errors-${new Date().toISOString().slice(0,10)}.csv`;
                      a.click();
                    }}
                    className="text-[10px] uppercase font-mono border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5 flex items-center gap-1">
                    <FileDown size={10} /> CSV
                  </button>
                </div>
              )}
            </div>
            {result.failed_rows.map((r, i) => (
              <div key={i} className="font-mono text-[10px] text-red-700 border-b border-red-100 py-0.5">
                Row {r.row} · {r.invoice_no || "—"} → {r.reason}
              </div>
            ))}
            {(result.auto_created_parties?.length || 0) > 0 && (
              <div className="mt-2 pt-2 border-t border-zinc-200" data-testid="import-auto-parties">
                <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700 mb-1">
                  {result.auto_created_parties.length} new part{result.auto_created_parties.length === 1 ? "y" : "ies"} auto-added — enrich details later in Customers
                </div>
                {result.auto_created_parties.map((p, i) => (
                  <div key={p.customer_id || i} className="font-mono text-[10px] text-orange-700 py-0.5">
                    Row {p.row} · {p.invoice_no} → new party: <b>{p.name}</b>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={result ? onDone : onClose} className="px-4 py-1.5 border border-zinc-300 text-xs uppercase font-mono">
            {result ? "Done" : "Cancel"}
          </button>
          <button type="submit" disabled={busy || !file} data-testid="import-submit"
            className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase font-mono disabled:opacity-50">
            {busy ? "Importing…" : "Import"}
          </button>
        </div>
      </form>
    </div>
  );
}

function FollowUpModal({ invoice, onClose, onDone }) {
  const [tag, setTag] = useState(invoice.follow_up || "");
  const [date, setDate] = useState(invoice.follow_up_date || "");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.patch(`/invoices/${invoice.id}/follow-up`, { follow_up: tag, follow_up_date: date });
      toast.success("Follow-up saved");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const clear = async () => {
    setBusy(true);
    try {
      await api.patch(`/invoices/${invoice.id}/follow-up`, { follow_up: "", follow_up_date: "" });
      toast.success("Follow-up cleared");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md p-6 space-y-3" data-testid="follow-up-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-yellow-700">Follow-up</div>
            <h2 className="text-lg font-bold tracking-tight">{invoice.invoice_no}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Tag / Status</div>
          <input data-testid="fu-tag" value={tag} onChange={(e) => setTag(e.target.value)} placeholder="e.g. 1st reminder sent, commit received, escalated"
            className={cls} />
        </label>
        <div className="flex flex-wrap gap-1">
          {["1st reminder sent", "2nd reminder", "commit received", "escalated", "cheque promised"].map((s) => (
            <button type="button" key={s} onClick={() => setTag(s)}
              className="text-[10px] font-mono border border-zinc-300 hover:bg-yellow-50 hover:border-yellow-500 px-2 py-1">
              {s}
            </button>
          ))}
        </div>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Next Follow-up Date (optional)</div>
          <input data-testid="fu-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className={cls} />
        </label>
        <div className="flex justify-between gap-2 pt-2 border-t border-zinc-200">
          {(invoice.follow_up || invoice.follow_up_date) && (
            <button type="button" onClick={clear} disabled={busy} data-testid="fu-clear"
              className="text-[11px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-50 px-3 py-1.5">
              Clear
            </button>
          )}
          <div className="flex-1" />
          <button type="button" onClick={onClose} className="px-4 py-1.5 border border-zinc-300 text-xs uppercase font-mono">Cancel</button>
          <button type="submit" disabled={busy} data-testid="fu-save"
            className="px-4 py-1.5 bg-yellow-600 hover:bg-yellow-700 text-white text-xs uppercase font-mono disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

function StatCard({ testid, icon, label, value, note, accent = "zinc", onClick }) {
  const border = {
    orange: "border-t-orange-600", red: "border-t-red-600",
    emerald: "border-t-emerald-600", zinc: "border-t-zinc-800",
    yellow: "border-t-yellow-500", amber: "border-t-amber-500",
    blue: "border-t-blue-600",
  }[accent] || "border-t-zinc-800";
  const Cmp = onClick ? "button" : "div";
  return (
    <Cmp data-testid={testid} onClick={onClick}
      className={`bg-white border border-zinc-300 border-t-4 ${border} px-4 py-3 text-left ${onClick ? "hover:bg-zinc-50 cursor-pointer transition-colors" : ""}`}>
      <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
        {icon}{label}
      </div>
      <div className="text-2xl font-bold tracking-tight text-zinc-950 mt-1">{value}</div>
      {note && <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{note}</div>}
    </Cmp>
  );
}

function InvoiceForm({ initial, companies, customers, onClose, onSaved }) {
  const today = new Date().toISOString().slice(0, 10);
  const [invoice_no, setInvoiceNo] = useState(initial?.invoice_no || "");
  const [company_id, setCompanyId] = useState(initial?.company_id || companies[0]?.id || "");
  const [customer_id, setCustomerId] = useState(initial?.customer_id || customers[0]?.id || "");
  const [invoice_date, setInvoiceDate] = useState(initial?.invoice_date || today);
  const [credit_days, setCreditDays] = useState(initial?.credit_days ?? "");
  const [gross_amount, setGross] = useState(initial?.gross_amount ?? "");
  const [notes, setNotes] = useState(initial?.notes || "");
  const [busy, setBusy] = useState(false);

  // Prefill credit_days from customer default whenever party changes
  useEffect(() => {
    if (initial) return; // don't override on edit
    const cust = customers.find((c) => c.id === customer_id);
    if (cust && (credit_days === "" || credit_days === null)) {
      setCreditDays(cust.default_credit_days || 30);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!invoice_no || !company_id || !customer_id || !gross_amount) {
      return toast.error("Invoice #, party, company and amount are required");
    }
    setBusy(true);
    try {
      const body = {
        invoice_no: invoice_no.trim(),
        company_id, customer_id, invoice_date,
        credit_days: credit_days === "" ? null : Number(credit_days),
        gross_amount: Number(gross_amount), notes,
      };
      if (initial) await api.put(`/invoices/${initial.id}`, body);
      else await api.post("/invoices", body);
      toast.success(initial ? "Invoice updated" : "Invoice created");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 space-y-4" data-testid="invoice-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">{initial ? "Edit" : "New"} Invoice</div>
            <h2 className="text-xl font-bold tracking-tight">{initial ? initial.invoice_no : "Add a manual invoice"}</h2>
          </div>
          <button type="button" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Invoice # *"><input data-testid="inv-no" required value={invoice_no} onChange={(e) => setInvoiceNo(e.target.value)} className={cls} /></Field>
          <Field label="Invoice Date *"><input data-testid="inv-date" type="date" required value={invoice_date} onChange={(e) => setInvoiceDate(e.target.value)} className={cls} /></Field>
          <Field label="Billing Firm *">
            <select data-testid="inv-company" required value={company_id} onChange={(e) => setCompanyId(e.target.value)} className={cls}>
              <option value="">— pick —</option>
              {companies.map((c) => (<option key={c.id} value={c.id}>{c.name}</option>))}
            </select>
          </Field>
          <Field label="Party (Customer) *">
            <select data-testid="inv-customer" required value={customer_id} onChange={(e) => setCustomerId(e.target.value)} className={cls}>
              <option value="">— pick —</option>
              {customers.map((c) => (<option key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</option>))}
            </select>
          </Field>
          <Field label="Credit Days">
            <input data-testid="inv-credit-days" type="number" min="0" max="365" value={credit_days} onChange={(e) => setCreditDays(e.target.value)}
              placeholder="from party default" className={cls} />
          </Field>
          <Field label="Gross Amount (₹) *">
            <input data-testid="inv-amount" type="number" min="0" step="0.01" required value={gross_amount} onChange={(e) => setGross(e.target.value)} className={cls} />
          </Field>
          <div className="md:col-span-2">
            <Field label="Notes">
              <textarea data-testid="inv-notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className={cls} />
            </Field>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-300 text-xs uppercase tracking-wider font-mono">Cancel</button>
          <button type="submit" disabled={busy} data-testid="inv-submit"
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50">
            {busy ? "Saving…" : (initial ? "Save" : "Create Invoice")}
          </button>
        </div>
      </form>
    </div>
  );
}

function CompanyForm({ initial, onClose, onSaved }) {
  const [s, setS] = useState({
    name: initial?.name || "", gst: initial?.gst || "", pan: initial?.pan || "",
    address: initial?.address || "", phone: initial?.phone || "", email: initial?.email || "",
    owner_email: initial?.owner_email || "",
    bank_name: initial?.bank_name || "", bank_account: initial?.bank_account || "", bank_ifsc: initial?.bank_ifsc || "",
    active: initial?.active !== false,
  });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!s.name.trim()) return toast.error("Company name required");
    setBusy(true);
    try {
      if (initial) await api.put(`/billing-companies/${initial.id}`, s);
      else await api.post("/billing-companies", s);
      toast.success(initial ? "Company updated" : "Company added");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={submit} onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-2xl max-h-[92vh] overflow-y-auto p-6 space-y-4" data-testid="company-form">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">{initial ? "Edit" : "New"} Selling Company</div>
            <h2 className="text-xl font-bold tracking-tight">Billing firm details</h2>
          </div>
          <button type="button" onClick={onClose}><X size={22} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Field label="Company Name *"><input data-testid="co-name" required value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })} className={cls} /></Field>
          <Field label="GST"><input data-testid="co-gst" value={s.gst} onChange={(e) => setS({ ...s, gst: e.target.value })} className={cls} /></Field>
          <Field label="PAN"><input value={s.pan} onChange={(e) => setS({ ...s, pan: e.target.value })} className={cls} /></Field>
          <Field label="Phone"><input value={s.phone} onChange={(e) => setS({ ...s, phone: e.target.value })} className={cls} /></Field>
          <Field label="Email"><input type="email" value={s.email} onChange={(e) => setS({ ...s, email: e.target.value })} className={cls} /></Field>
          <Field label="Owner email (weekly digest)"><input data-testid="co-owner-email" type="email" placeholder="owner@wellpack.in — receives Monday digest" value={s.owner_email} onChange={(e) => setS({ ...s, owner_email: e.target.value })} className={cls} /></Field>
          <div className="md:col-span-2">
            <Field label="Address"><textarea rows={2} value={s.address} onChange={(e) => setS({ ...s, address: e.target.value })} className={cls} /></Field>
          </div>
          <Field label="Bank Name"><input value={s.bank_name} onChange={(e) => setS({ ...s, bank_name: e.target.value })} className={cls} /></Field>
          <Field label="Bank A/C"><input value={s.bank_account} onChange={(e) => setS({ ...s, bank_account: e.target.value })} className={cls} /></Field>
          <Field label="IFSC"><input value={s.bank_ifsc} onChange={(e) => setS({ ...s, bank_ifsc: e.target.value })} className={cls} /></Field>
        </div>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={onClose} className="px-4 py-2 border border-zinc-300 text-xs uppercase tracking-wider font-mono">Cancel</button>
          <button type="submit" disabled={busy} data-testid="co-submit"
            className="px-4 py-2 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase tracking-wider font-mono disabled:opacity-50">
            {busy ? "Saving…" : (initial ? "Save" : "Add Company")}
          </button>
        </div>
      </form>
    </div>
  );
}

function PaymentModal({ invoice, onClose, onDone }) {
  const { user } = useAuth();
  const canManage = ["admin", "account"].includes(user?.role);
  const today = new Date().toISOString().slice(0, 10);
  const [payments, setPayments] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [date, setDate] = useState(today);
  const [amount, setAmount] = useState(invoice.balance || "");
  const [mode, setMode] = useState("bank");
  const [ref_no, setRef] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [advance, setAdvance] = useState({ balance: 0 });
  const [siblings, setSiblings] = useState([]); // other unpaid invoices for cascade preview

  const reloadPayments = async () => {
    const r = await api.get(`/invoices/${invoice.id}/payments`);
    setPayments(r.data);
  };
  const reloadAdvance = async () => {
    try {
      const r = await api.get(`/customers/${invoice.customer_id}/advance${invoice.company_id ? `?company_id=${encodeURIComponent(invoice.company_id)}` : ""}`);
      setAdvance(r.data);
    } catch { setAdvance({ balance: 0 }); }
  };
  const reloadSiblings = async () => {
    try {
      const r = await api.get(`/invoices?customer_id=${invoice.customer_id}${invoice.company_id ? `&company_id=${encodeURIComponent(invoice.company_id)}` : ""}`);
      const others = (r.data || [])
        .filter((i) => i.id !== invoice.id && i.status !== "void" && (i.balance || 0) > 0.01)
        .sort((a, b) => (a.invoice_date || "").localeCompare(b.invoice_date || ""));
      setSiblings(others);
    } catch { setSiblings([]); }
  };
  useEffect(() => { reloadPayments(); reloadAdvance(); reloadSiblings(); /* eslint-disable-next-line */ }, [invoice.id]);

  // Live preview of how the current amount will cascade.
  const cascadePreview = useMemo(() => {
    const amt = Number(amount) || 0;
    if (!amt || editingId) return null;
    const bal = Number(invoice.balance || 0);
    const primary = Math.min(amt, bal);
    let remainder = Math.max(0, amt - primary);
    const legs = [];
    for (const sib of siblings) {
      if (remainder <= 0.01) break;
      const take = Math.min(remainder, Number(sib.balance || 0));
      if (take > 0.01) {
        legs.push({ invoice_no: sib.invoice_no, invoice_id: sib.id, amount: take });
        remainder = Math.max(0, remainder - take);
      }
    }
    const advance_add = remainder > 0.01 ? Math.round(remainder * 100) / 100 : 0;
    return { primary, legs, advance_add };
  }, [amount, invoice.balance, siblings, editingId]);

  const resetForm = () => {
    setEditingId(null); setDate(today); setAmount(invoice.balance || "");
    setMode("bank"); setRef(""); setNote("");
  };
  const startEdit = (p) => {
    setEditingId(p.id);
    setDate(p.date || today); setAmount(p.amount); setMode(p.mode || "bank");
    setRef(p.ref_no || ""); setNote(p.note || "");
  };
  const deletePay = async (p) => {
    if (!window.confirm(`Delete payment of ₹${p.amount.toLocaleString("en-IN")} on ${p.date}?`)) return;
    try {
      await api.delete(`/invoices/${invoice.id}/payments/${p.id}`);
      toast.success("Payment deleted");
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const savePay = async (e) => {
    e.preventDefault();
    const amt = Number(amount);
    if (!amt || amt <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/invoices/${invoice.id}/payments/${editingId}`, { date, amount: amt, mode, ref_no, note });
        toast.success("Payment updated");
      } else {
        const { data } = await api.post(`/invoices/${invoice.id}/payments`, { date, amount: amt, mode, ref_no, note });
        toast.success(`₹${amt.toLocaleString("en-IN")} recorded`);
        if ((data.cascaded || []).length > 0) {
          toast.info(`Auto-adjusted to ${data.cascaded.length} other invoice(s): ${data.cascaded.map((c) => c.invoice_no).join(", ")}`);
        }
        if ((data.advance_added || 0) > 0.01) {
          toast.info(`₹${data.advance_added.toLocaleString("en-IN")} added as advance for the party`);
        }
      }
      onDone();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-lg max-h-[92vh] overflow-y-auto p-6 space-y-4" data-testid="payment-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">
              {editingId ? "Edit Payment" : "Record Payment"}
            </div>
            <h2 className="text-lg font-bold tracking-tight">{invoice.invoice_no}</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-[11px] font-mono">
          <div className="bg-zinc-100 p-2"><div className="text-zinc-500 uppercase tracking-widest">Gross</div><div className="text-zinc-900 font-bold">₹ {invoice.gross_amount.toLocaleString("en-IN")}</div></div>
          <div className="bg-emerald-50 p-2"><div className="text-emerald-700 uppercase tracking-widest">Paid</div><div className="text-emerald-900 font-bold">₹ {invoice.paid_amount.toLocaleString("en-IN")}</div></div>
          <div className="bg-orange-50 p-2"><div className="text-orange-700 uppercase tracking-widest">Balance</div><div className="text-orange-900 font-bold">₹ {invoice.balance.toLocaleString("en-IN")}</div></div>
        </div>
        {advance.balance > 0.01 && (
          <div className="border-2 border-blue-400 bg-blue-50 px-3 py-2 text-[11px] font-mono flex items-center justify-between" data-testid="advance-banner">
            <span className="text-blue-800">
              Party has <b>₹ {advance.balance.toLocaleString("en-IN")}</b> advance on account.
            </span>
            <span className="text-[10px] text-blue-700 uppercase tracking-widest">auto-applies on next new invoice</span>
          </div>
        )}
        {cascadePreview && cascadePreview.primary >= 0 && Number(amount) > Number(invoice.balance || 0) + 0.01 && (
          <div className="border border-orange-400 bg-orange-50 p-3 text-[11px] font-mono space-y-1" data-testid="cascade-preview">
            <div className="text-orange-800 font-bold uppercase tracking-widest text-[10px]">Excess payment — will be auto-adjusted</div>
            <div className="text-zinc-800">→ This invoice: <b>₹ {cascadePreview.primary.toLocaleString("en-IN")}</b> (fully cleared)</div>
            {cascadePreview.legs.map((leg, i) => (
              <div key={i} className="text-zinc-800">→ {leg.invoice_no}: <b>₹ {leg.amount.toLocaleString("en-IN")}</b></div>
            ))}
            {cascadePreview.advance_add > 0.01 && (
              <div className="text-blue-800">→ Advance for party: <b>₹ {cascadePreview.advance_add.toLocaleString("en-IN")}</b> (auto-applies next new invoice)</div>
            )}
          </div>
        )}
        <form onSubmit={savePay} className="space-y-2 border-t border-zinc-200 pt-3">
          <div className="grid grid-cols-2 gap-2">
            <Field label="Date"><input data-testid="pay-date" type="date" required value={date} onChange={(e) => setDate(e.target.value)} className={cls} /></Field>
            <Field label="Amount ₹"><input data-testid="pay-amount" type="number" min="0" step="0.01" required value={amount} onChange={(e) => setAmount(e.target.value)} className={cls} /></Field>
            <Field label="Mode">
              <select data-testid="pay-mode" value={mode} onChange={(e) => setMode(e.target.value)} className={cls}>
                <option value="bank">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="cheque">Cheque</option>
                <option value="adjust">Adjustment</option>
              </select>
            </Field>
            <Field label="Ref No"><input data-testid="pay-ref" value={ref_no} onChange={(e) => setRef(e.target.value)} placeholder="NEFT / UPI / cheque #" className={cls} /></Field>
          </div>
          <Field label="Note"><input data-testid="pay-note" value={note} onChange={(e) => setNote(e.target.value)} className={cls} /></Field>
          <div className="flex gap-2 mt-2">
            {editingId && (
              <button type="button" onClick={resetForm} data-testid="pay-cancel-edit"
                className="px-3 py-2 border border-zinc-300 text-[11px] uppercase font-mono">Cancel edit</button>
            )}
            <button type="submit" disabled={busy} data-testid="pay-submit"
              className={`flex-1 ${editingId ? "bg-blue-600 hover:bg-blue-700" : "bg-emerald-600 hover:bg-emerald-700"} text-white px-3 py-2 text-xs uppercase tracking-wider font-mono disabled:opacity-50`}>
              {busy ? "Saving…" : (editingId ? "Update Payment" : "Record Payment")}
            </button>
          </div>
        </form>
        {payments.length > 0 && (
          <div className="border-t border-zinc-200 pt-3">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Previous Payments · click Edit to modify</div>
            <div className="max-h-48 overflow-y-auto text-xs font-mono">
              {payments.map((p) => {
                const isCascade = p.source === "cascade";
                const isAdvance = p.source === "advance_applied" || p.mode === "advance";
                return (
                <div key={p.id} className={`flex items-center justify-between border-b border-zinc-100 py-1 gap-2 ${editingId === p.id ? "bg-blue-50" : ""}`} data-testid={`pay-row-${p.id}`}>
                  <div className="flex-1 min-w-0 truncate">
                    {p.date} · <b className={isAdvance ? "text-blue-700" : "text-emerald-700"}>₹ {p.amount.toLocaleString("en-IN")}</b> · {p.mode}{p.ref_no ? ` #${p.ref_no}` : ""}
                    {isCascade && <span className="ml-1 text-[9px] uppercase bg-orange-100 text-orange-800 px-1 py-0.5 border border-orange-300">auto-cascade</span>}
                    {isAdvance && <span className="ml-1 text-[9px] uppercase bg-blue-100 text-blue-800 px-1 py-0.5 border border-blue-300">from advance</span>}
                    {p.note && <span className="text-zinc-500"> — {p.note}</span>}
                  </div>
                  {canManage && (
                    <div className="flex gap-1 shrink-0">
                      <button data-testid={`pay-edit-${p.id}`} onClick={() => startEdit(p)}
                        className="inline-flex items-center gap-1 text-[9px] uppercase border border-blue-500 text-blue-700 hover:bg-blue-50 px-1.5 py-0.5"
                        title={isAdvance ? "Editing keeps the advance ledger in sync" : isCascade ? "Editing an auto-cascade entry" : "Edit payment"}>
                        <Pencil size={9} /> Edit
                      </button>
                      <button data-testid={`pay-del-${p.id}`} onClick={() => deletePay(p)}
                        className="inline-flex items-center gap-1 text-[9px] uppercase border border-red-500 text-red-700 hover:bg-red-50 px-1.5 py-0.5"
                        title={isAdvance ? "Deletes payment + restores the advance" : "Delete payment"}>
                        <Trash2 size={9} />
                      </button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const cls = "w-full h-9 px-2 border border-zinc-300 text-sm bg-white focus:border-orange-600 outline-none";
function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">{label}</div>
      {children}
    </label>
  );
}

// -----------------------------------------------------------------------------
// Reminder templates — manage reusable WhatsApp messages per aging bucket
// -----------------------------------------------------------------------------
export function ReminderTemplatesPanel() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null); // template being edited, or {new: true}
  const load = async () => {
    try { const { data } = await api.get("/reminder-templates"); setItems(data); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/reminder-templates/${t.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };
  const grouped = AGING_BUCKET_LABELS.map((b) => ({ bucket: b, list: items.filter((t) => t.bucket === b) }));
  return (
    <div className="space-y-3" data-testid="templates-panel">
      <div className="flex items-center justify-between">
        <p className="text-[11px] text-zinc-600">
          Placeholders: <span className="font-mono">{"{customer_name} {invoice_no} {amount} {balance} {days_overdue} {due_date} {company_name}"}</span>
        </p>
        <button data-testid="tpl-new-btn" onClick={() => setEdit({ new: true, bucket: "0-15", name: "", message: "", is_active: true })}
          className="bg-orange-600 hover:bg-orange-700 text-white px-3 py-1.5 text-xs uppercase font-mono flex items-center gap-1">
          <Plus size={14} /> New
        </button>
      </div>
      {grouped.map(({ bucket, list }) => (
        <div key={bucket} className="border border-zinc-200">
          <div className="bg-zinc-100 px-3 py-1.5 text-[11px] font-mono uppercase tracking-widest text-zinc-700 flex items-center justify-between">
            <span>Bucket {bucket} days · <b>{list.length}</b> template(s)</span>
          </div>
          {list.length === 0 ? (
            <div className="p-3 text-[11px] text-zinc-500 font-mono">No templates. Click "New" and set bucket to {bucket}.</div>
          ) : list.map((t) => (
            <div key={t.id} className="p-3 border-t border-zinc-200 flex items-start justify-between gap-3" data-testid={`tpl-${t.id}`}>
              <div className="min-w-0 flex-1">
                <div className="text-sm font-semibold text-zinc-900 flex items-center gap-2">
                  {t.name}
                  {t.is_default && <span className="text-[9px] font-mono uppercase bg-zinc-200 text-zinc-700 px-1.5 py-0.5">seed</span>}
                  {!t.is_active && <span className="text-[9px] font-mono uppercase bg-red-100 text-red-700 px-1.5 py-0.5">inactive</span>}
                </div>
                <pre className="text-[11px] whitespace-pre-wrap text-zinc-700 mt-1 font-mono max-h-24 overflow-y-auto">{t.message}</pre>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid={`tpl-edit-${t.id}`} onClick={() => setEdit(t)}
                  className="text-[10px] uppercase font-mono border border-blue-500 text-blue-700 hover:bg-blue-50 px-2 py-1 flex items-center gap-1">
                  <Pencil size={10} /> Edit
                </button>
                <button data-testid={`tpl-del-${t.id}`} onClick={() => remove(t)}
                  className="text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-50 px-2 py-1 flex items-center gap-1">
                  <Trash2 size={10} /> Del
                </button>
              </div>
            </div>
          ))}
        </div>
      ))}
      {edit && (
        <TemplateFormModal initial={edit} onClose={() => setEdit(null)} onSaved={() => { setEdit(null); load(); }} />
      )}
    </div>
  );
}

export function ReminderTemplatesModal({ onClose }) {
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 space-y-3" data-testid="templates-modal">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Payments</div>
            <h2 className="text-lg font-bold tracking-tight">Reminder Templates</h2>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <ReminderTemplatesPanel />
      </div>
    </div>
  );
}

function TemplateFormModal({ initial, onClose, onSaved }) {
  const isNew = !!initial.new;
  const [s, setS] = useState({
    bucket: initial.bucket || "0-15",
    name: initial.name || "",
    message: initial.message || "",
    is_active: initial.is_active ?? true,
  });
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (!s.name.trim() || !s.message.trim()) return toast.error("Name and message are required");
    setBusy(true);
    try {
      if (isNew) await api.post("/reminder-templates", s);
      else await api.put(`/reminder-templates/${initial.id}`, s);
      toast.success(isNew ? "Template created" : "Template updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-lg p-6 space-y-3" data-testid="tpl-form">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-bold">{isNew ? "New" : "Edit"} template</h3>
          <button type="button" onClick={onClose}><X size={20} /></button>
        </div>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Aging bucket</div>
          <select data-testid="tpl-bucket" value={s.bucket} onChange={(e) => setS({ ...s, bucket: e.target.value })}
            className="w-full text-sm border border-zinc-300 px-2 py-1.5">
            {AGING_BUCKET_LABELS.map((b) => <option key={b} value={b}>{b} days</option>)}
          </select>
        </label>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Name</div>
          <input data-testid="tpl-name" value={s.name} onChange={(e) => setS({ ...s, name: e.target.value })}
            className="w-full text-sm border border-zinc-300 px-2 py-1.5" placeholder="Polite reminder" />
        </label>
        <label className="block">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Message (placeholders in curly braces)</div>
          <textarea data-testid="tpl-message" value={s.message} onChange={(e) => setS({ ...s, message: e.target.value })}
            rows={8} className="w-full text-sm border border-zinc-300 px-2 py-1.5 font-mono" />
        </label>
        <label className="flex items-center gap-2 text-xs font-mono">
          <input type="checkbox" checked={s.is_active} onChange={(e) => setS({ ...s, is_active: e.target.checked })} />
          Active (show in WA picker)
        </label>
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={onClose} className="px-4 py-1.5 border border-zinc-300 text-xs uppercase font-mono">Cancel</button>
          <button type="submit" disabled={busy} data-testid="tpl-save"
            className="px-4 py-1.5 bg-orange-600 hover:bg-orange-700 text-white text-xs uppercase font-mono disabled:opacity-50">
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </form>
    </div>
  );
}

// -----------------------------------------------------------------------------
// WA picker — always ask which template to send, even when only one is defined
// -----------------------------------------------------------------------------
function WaPickerModal({ invoice, customer, company, onClose }) {
  const isNotDue = (invoice.days_overdue || 0) === 0;
  const bucket = bucketForInvoice(invoice);
  const bucketLabel = isNotDue ? "Not Due" : `${bucket} days`;
  const [templates, setTemplates] = useState([]);
  const [pickedId, setPickedId] = useState("");
  const [text, setText] = useState("");
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/reminder-templates?bucket=${encodeURIComponent(bucket)}`);
        const active = data.filter((t) => t.is_active !== false);
        setTemplates(active);
        if (active[0]) setPickedId(active[0].id);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const ctx = {
    customer_name: customer.name || "Sir/Madam",
    invoice_no: invoice.invoice_no,
    amount: invoice.gross_amount,
    balance: invoice.balance,
    days_overdue: invoice.days_overdue,
    due_date: invoice.due_date,
    company_name: company.name || "Wellpack Polyplast",
  };
  useEffect(() => {
    const t = templates.find((x) => x.id === pickedId);
    if (t) setText(renderTemplate(t.message, ctx));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pickedId, templates]);

  const phone = (customer.phone || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  const waHref = phone ? `https://wa.me/${phone}?text=${encodeURIComponent(text)}` : null;
  const send = async () => {
    if (!waHref) return toast.error("Party has no phone");
    if (!text.trim()) return toast.error("Message is empty");
    // Persist a lightweight reminder log so the Party Focus timeline shows
    // every WA sent (channel=whatsapp, outcome=wa_sent). Best-effort — a
    // network hiccup shouldn't block opening WhatsApp.
    try {
      await api.post("/call-logs", {
        customer_id: customer.id,
        invoice_id: invoice.id,
        date: new Date().toISOString().slice(0, 10),
        channel: "whatsapp",
        outcome: "wa_sent",
        notes: `Template · ${(templates.find((x) => x.id === pickedId) || {}).name || "custom"}`,
      });
    } catch (_) { /* non-blocking */ }
    window.open(waHref, "_blank", "noreferrer");
    onClose();
  };
  const copyMsg = async () => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied to clipboard"); }
    catch { toast.error("Copy failed"); }
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-xl p-6 space-y-3" data-testid="wa-picker">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">
              WhatsApp reminder · bucket {bucketLabel}
            </div>
            <h2 className="text-lg font-bold tracking-tight">
              {invoice.invoice_no} · {customer.name || "—"}
            </h2>
            <div className="text-[11px] text-zinc-600 font-mono">
              Balance ₹ {(invoice.balance || 0).toLocaleString("en-IN")} ·
              {invoice.days_overdue > 0 ? ` ${invoice.days_overdue}d overdue` : ` due ${invoice.due_date}`}
            </div>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        {templates.length === 0 ? (
          <div className="bg-yellow-50 border border-yellow-400 p-3 text-xs text-yellow-800">
            No active templates for bucket <b>{bucket}</b>. Add one from the "Templates" button on the Payments page.
          </div>
        ) : (
          <>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Choose template</div>
              <select data-testid="wa-tpl-select" value={pickedId} onChange={(e) => setPickedId(e.target.value)}
                className="w-full text-sm border border-zinc-300 px-2 py-1.5">
                {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </label>
            <label className="block">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">Message preview (editable)</div>
              <textarea data-testid="wa-preview" value={text} onChange={(e) => setText(e.target.value)}
                rows={9} className="w-full text-sm border border-zinc-300 px-2 py-1.5 font-mono" />
            </label>
            {!phone && (
              <div className="text-[11px] text-red-700 font-mono">
                No phone number on this party — add one under Customers to send.
              </div>
            )}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
          <button type="button" onClick={copyMsg} data-testid="wa-copy"
            className="px-3 py-1.5 border border-zinc-300 text-xs uppercase font-mono flex items-center gap-1">
            <Copy size={12} /> Copy
          </button>
          <button type="button" onClick={send} disabled={!waHref || !templates.length} data-testid="wa-send"
            className="px-4 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs uppercase font-mono disabled:opacity-50 flex items-center gap-1">
            <MessageCircle size={12} /> Open WhatsApp
          </button>
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Invoice list modal — opens a separate, scrollable list for any dashboard tile
// or aging-table cell. Kept independent of the main invoice grid below so the
// list stays visible while the operator works (Record, Edit, WA, Statement).
// -----------------------------------------------------------------------------
function InvoiceListModal({ title, tone = "orange", invoices, cust, comp, onOpenStatement, onRecord, onEdit, onWa, onClose }) {
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return invoices;
    return invoices.filter((i) => {
      const c = cust?.(i.customer_id);
      const co = comp?.(i.company_id);
      return (
        (i.invoice_no || "").toLowerCase().includes(t) ||
        (c?.name || "").toLowerCase().includes(t) ||
        (co?.name || "").toLowerCase().includes(t)
      );
    });
  }, [q, invoices, cust, comp]);

  const totals = useMemo(() => {
    const gross = filtered.reduce((s, i) => s + (i.gross_amount || 0), 0);
    const paid = filtered.reduce((s, i) => s + (i.paid_amount || 0), 0);
    const balance = filtered.reduce((s, i) => s + (i.balance || 0), 0);
    return { gross, paid, balance };
  }, [filtered]);
  const fmt = (n) => (n || 0).toLocaleString("en-IN");

  const headerTone = {
    orange: "text-orange-700", red: "text-red-700",
    emerald: "text-emerald-700", yellow: "text-yellow-700",
    amber: "text-amber-700", zinc: "text-zinc-700",
  }[tone] || "text-zinc-700";

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-6xl my-6 border border-zinc-300 flex flex-col max-h-[92vh]"
        data-testid="invoice-list-modal">
        <div className="flex items-center justify-between border-b border-zinc-300 px-5 py-3 bg-zinc-50">
          <div>
            <div className={`text-[10px] font-mono uppercase tracking-widest ${headerTone}`}>Payments</div>
            <h2 className="text-lg font-bold tracking-tight">{title}</h2>
            <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
              {filtered.length} invoice{filtered.length === 1 ? "" : "s"} · Gross ₹ {fmt(totals.gross)} · Paid ₹ {fmt(totals.paid)} · <b className={filtered.length ? "text-orange-700" : ""}>Balance ₹ {fmt(totals.balance)}</b>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <input data-testid="invlist-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search #, party, firm…"
              className="h-9 px-2 border border-zinc-300 text-sm bg-white font-mono w-52" />
            <button onClick={onClose} data-testid="invlist-close"><X size={20} /></button>
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {filtered.length === 0 ? (
            <div className="p-10 text-center text-zinc-500 text-sm">No invoices in this list.</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-zinc-100 text-zinc-600 uppercase text-[9px] font-mono tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Invoice #</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Party</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Billing firm</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Date</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Due</th>
                  <th className="text-right px-3 py-2 border-b border-zinc-200">Gross ₹</th>
                  <th className="text-right px-3 py-2 border-b border-zinc-200">Paid ₹</th>
                  <th className="text-right px-3 py-2 border-b border-zinc-200">Balance ₹</th>
                  <th className="text-left px-3 py-2 border-b border-zinc-200">Action</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {filtered.map((i) => {
                  const c = cust?.(i.customer_id);
                  const co = comp?.(i.company_id);
                  const isOver = i.days_overdue > 0 && i.balance > 0.01;
                  return (
                    <tr key={i.id} className={`border-b border-zinc-100 ${isOver ? "bg-red-50/40" : ""}`} data-testid={`invlist-row-${i.id}`}>
                      <td className="px-3 py-2 font-semibold">{i.invoice_no}</td>
                      <td className="px-3 py-2 font-sans">{c?.name || "—"}</td>
                      <td className="px-3 py-2 font-sans text-zinc-600 text-[11px]">{co?.name || "—"}</td>
                      <td className="px-3 py-2">{i.invoice_date}</td>
                      <td className={`px-3 py-2 ${isOver ? "text-red-700 font-semibold" : ""}`}>
                        {i.due_date}
                        {isOver && <div className="text-[9px] text-red-700">↑ {i.days_overdue}d over</div>}
                      </td>
                      <td className="px-3 py-2 text-right">{fmt(i.gross_amount)}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{fmt(i.paid_amount)}</td>
                      <td className={`px-3 py-2 text-right font-bold ${i.balance > 0.01 ? "text-orange-800" : "text-zinc-400"}`}>{fmt(i.balance)}</td>
                      <td className="px-3 py-2">
                        <div className="flex gap-1">
                          {onOpenStatement && (
                            <button data-testid={`invlist-stmt-${i.id}`} onClick={() => onOpenStatement(i.customer_id, i.company_id)}
                              className="text-[10px] uppercase font-mono border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5">STMT</button>
                          )}
                          {onWa && (c?.phone || "").trim() && i.balance > 0.01 && (
                            <button data-testid={`invlist-wa-${i.id}`} onClick={() => onWa(i)}
                              className="text-[10px] uppercase font-mono border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-0.5 inline-flex items-center gap-1">
                              <MessageCircle size={10} /> WA
                            </button>
                          )}
                          {onRecord && i.balance > 0.01 && (
                            <button data-testid={`invlist-record-${i.id}`} onClick={() => onRecord(i)}
                              className="text-[10px] uppercase font-mono border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-0.5 inline-flex items-center gap-1">
                              <IndianRupee size={10} /> Record
                            </button>
                          )}
                          {onEdit && (
                            <button data-testid={`invlist-edit-${i.id}`} onClick={() => onEdit(i)}
                              className="text-[10px] uppercase font-mono border border-blue-600 text-blue-700 hover:bg-blue-50 px-2 py-0.5 inline-flex items-center gap-1">
                              <Pencil size={10} /> Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Schedule tab — two side-by-side planners:
//   LEFT  · Date-wise Follow-up Schedule (grouped by relative-date buckets)
//   RIGHT · Future Payment Inflow (best-case forecast, settable window)
// Both planners honour the top-of-page company scope automatically because
// `invoices` is already filtered by the parent load().
// -----------------------------------------------------------------------------
function ScheduleTab({ invoices, companyFilter, cust, onOpenInvoice, onFollowUp, onWa }) {
  const [orgView, setOrgView] = useState("date"); // "date" | "party" — follow-up organiser view
  const [showFuPicker, setShowFuPicker] = useState(false);
  // Bulk-reschedule state
  const [selected, setSelected] = useState(() => new Set());
  const [bulkShift, setBulkShift] = useState("");
  const [bulkDate, setBulkDate] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const toggleSel = (id) => setSelected((prev) => {
    const s = new Set(prev); s.has(id) ? s.delete(id) : s.add(id); return s;
  });
  const clearSel = () => setSelected(new Set());

  // Bulk-reschedule: sends every selected invoice through one API call. The
  // caller decides whether to shift by ±N days or reset to an absolute date;
  // the backend accepts either but not both, so we pick based on which input
  // was filled in most recently.
  const doBulkReschedule = async (kind) => {
    if (selected.size === 0) return toast.error("Nothing selected");
    const payload = { invoice_ids: [...selected] };
    if (kind === "shift") {
      const n = parseInt(bulkShift, 10);
      if (!n || Math.abs(n) > 366) return toast.error("Enter shift days between -366 and +366");
      payload.shift_days = n;
    } else {
      if (!bulkDate) return toast.error("Pick a date");
      payload.new_date = bulkDate;
    }
    setBulkBusy(true);
    try {
      const { data } = await api.post("/invoices/follow-ups/bulk-reschedule", payload);
      toast.success(`${data.updated} follow-up(s) rescheduled`);
      clearSel(); setBulkShift(""); setBulkDate("");
      // Optimistic refresh — the parent page re-loads invoices via its own
      // effect chain when the user next changes any filter; we nudge it by
      // firing a router-like event. Simpler: rely on parent useEffect on
      // returning to Invoices tab. For immediate feedback we also mutate the
      // local invoice list.
      data.rows.forEach((r) => {
        const inv = invoices.find((i) => i.id === r.id);
        if (inv) inv.follow_up_date = r.follow_up_date;
      });
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBulkBusy(false); }
  };

  // ---------- Follow-up buckets ----------
  const followUpBuckets = useMemo(() => {
    const today = new Date(); today.setHours(0,0,0,0);
    const todayIso = today.toISOString().slice(0, 10);
    const tomorrow = new Date(today); tomorrow.setDate(today.getDate() + 1);
    const tomorrowIso = tomorrow.toISOString().slice(0, 10);
    const weekEnd = new Date(today); weekEnd.setDate(today.getDate() + 7);
    const weekEndIso = weekEnd.toISOString().slice(0, 10);
    const nextWeekEnd = new Date(today); nextWeekEnd.setDate(today.getDate() + 14);
    const nextWeekEndIso = nextWeekEnd.toISOString().slice(0, 10);

    const b = {
      overdue: [],   // follow_up_date < today AND not paid
      today: [],     // == today
      tomorrow: [],  // == tomorrow
      this_week: [], // (today, weekEnd]
      next_week: [], // (weekEnd, nextWeekEnd]
      later: [],     // > nextWeekEnd
      no_date: [],   // has follow_up tag but no date
    };
    invoices.forEach((i) => {
      if (i.status === "paid" || i.status === "void") return;
      const tag = (i.follow_up || "").trim();
      const d = (i.follow_up_date || "").trim();
      if (!tag && !d) return;
      if (!d) { b.no_date.push(i); return; }
      if (d < todayIso) b.overdue.push(i);
      else if (d === todayIso) b.today.push(i);
      else if (d === tomorrowIso) b.tomorrow.push(i);
      else if (d <= weekEndIso) b.this_week.push(i);
      else if (d <= nextWeekEndIso) b.next_week.push(i);
      else b.later.push(i);
    });
    return b;
  }, [invoices]);

  // Group follow-ups within a bucket by party for the collapsed party-wise view
  const byParty = useMemo(() => {
    const map = new Map();
    invoices.forEach((i) => {
      if (i.status === "paid" || i.status === "void") return;
      if (!(i.follow_up || "").trim() && !(i.follow_up_date || "").trim()) return;
      const c = cust?.(i.customer_id);
      const key = c?.name || "Unknown";
      if (!map.has(key)) map.set(key, { party: key, phone: c?.phone || "", invoices: [] });
      map.get(key).invoices.push(i);
    });
    return Array.from(map.values()).sort((a, b) => (b.invoices.length - a.invoices.length));
  }, [invoices, cust]);

  const totalFollowUps = Object.values(followUpBuckets).reduce((s, arr) => s + arr.length, 0);
  const fmt = (n) => (Math.round(n) || 0).toLocaleString("en-IN");

  return (
    <div className="space-y-4" data-testid="schedule-tab">
      <div className="grid grid-cols-1 gap-4">
        {/* ---- Follow-up Schedule ---- */}
        <div className="bg-white border border-zinc-300" data-testid="followup-schedule">
          <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Follow-up organiser</div>
              <div className="text-sm font-bold">
                {orgView === "date" ? "Date-wise" : "Party-wise"} · <b>{totalFollowUps}</b> scheduled
              </div>
            </div>
            <div className="flex items-center gap-1 flex-wrap">
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mr-1">View</span>
              {[
                { k: "date",  label: "By Date" },
                { k: "party", label: "By Party" },
              ].map((v) => (
                <button key={v.k} data-testid={`org-view-${v.k}`} onClick={() => setOrgView(v.k)}
                  className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${orgView === v.k ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>
                  {v.label}
                </button>
              ))}
              <button data-testid="new-followup-btn" onClick={() => setShowFuPicker(true)}
                className="ml-1 text-[10px] font-mono uppercase bg-orange-600 text-white border border-orange-600 hover:bg-orange-700 px-2 py-0.5 inline-flex items-center gap-1">
                <Plus size={10} /> New follow-up
              </button>
              <span className="ml-2 text-[10px] font-mono uppercase text-zinc-500">
                {companyFilter ? "Firm-scoped" : "All firms"}
              </span>
            </div>
          </div>

          {/* Bulk-reschedule toolbar — shown when at least one row is ticked. */}
          {selected.size > 0 && (
            <div className="border-b border-orange-300 bg-orange-50 px-3 py-2 flex flex-wrap items-center gap-2" data-testid="bulk-reschedule-bar">
              <span className="text-[10px] font-mono uppercase tracking-widest text-orange-800 font-semibold">
                {selected.size} selected
              </span>
              <button onClick={clearSel} className="text-[10px] font-mono uppercase text-zinc-600 hover:text-zinc-900 underline">Clear</button>
              <span className="text-zinc-300">|</span>
              <span className="text-[10px] font-mono uppercase text-zinc-600">Shift by</span>
              {[-7, -3, 3, 7, 14].map((n) => (
                <button key={n} disabled={bulkBusy} data-testid={`bulk-shift-${n}`}
                  onClick={() => { setBulkShift(String(n)); setTimeout(() => doBulkReschedule("shift"), 0); }}
                  className="text-[10px] font-mono uppercase border border-zinc-400 bg-white text-zinc-800 hover:bg-zinc-100 px-2 py-0.5 disabled:opacity-50">
                  {n > 0 ? `+${n}d` : `${n}d`}
                </button>
              ))}
              <input type="number" min={-366} max={366} value={bulkShift}
                onChange={(e) => setBulkShift(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") doBulkReschedule("shift"); }}
                placeholder="±N"
                data-testid="bulk-shift-input"
                className="w-16 h-6 px-1.5 border border-zinc-300 text-xs font-mono text-center" />
              <button disabled={bulkBusy || !bulkShift} data-testid="bulk-shift-apply"
                onClick={() => doBulkReschedule("shift")}
                className="text-[10px] font-mono uppercase bg-orange-600 text-white border border-orange-600 hover:bg-orange-700 px-2 py-0.5 disabled:opacity-50">
                Shift
              </button>
              <span className="text-zinc-300">|</span>
              <span className="text-[10px] font-mono uppercase text-zinc-600">Set to</span>
              <input type="date" value={bulkDate} onChange={(e) => setBulkDate(e.target.value)}
                data-testid="bulk-date-input"
                className="h-6 px-1 border border-zinc-300 text-xs font-mono" />
              <button disabled={bulkBusy || !bulkDate} data-testid="bulk-date-apply"
                onClick={() => doBulkReschedule("date")}
                className="text-[10px] font-mono uppercase bg-orange-600 text-white border border-orange-600 hover:bg-orange-700 px-2 py-0.5 disabled:opacity-50">
                Set date
              </button>
            </div>
          )}
          <div className="p-3 space-y-3 max-h-[70vh] overflow-y-auto">
            {orgView === "date" && [
              { key: "overdue",   label: "Overdue follow-ups", tone: "red" },
              { key: "today",     label: "Today",              tone: "orange" },
              { key: "tomorrow",  label: "Tomorrow",           tone: "amber" },
              { key: "this_week", label: "This week (next 7d)", tone: "yellow" },
              { key: "next_week", label: "Next week (8–14d)",   tone: "blue" },
              { key: "later",     label: "Later (>14d)",        tone: "zinc" },
              { key: "no_date",   label: "Tagged (no date)",    tone: "zinc" },
            ].map(({ key, label, tone }) => {
              const list = followUpBuckets[key];
              if (!list.length) return null;
              const toneCls = {
                red: "border-red-500 bg-red-50 text-red-800",
                orange: "border-orange-500 bg-orange-50 text-orange-800",
                amber: "border-amber-400 bg-amber-50 text-amber-800",
                yellow: "border-yellow-400 bg-yellow-50 text-yellow-800",
                blue: "border-blue-400 bg-blue-50 text-blue-800",
                zinc: "border-zinc-300 bg-zinc-50 text-zinc-700",
              }[tone];
              return (
                <div key={key} data-testid={`fu-bucket-${key}`}>
                  <div className={`text-[10px] font-mono uppercase tracking-widest px-2 py-1 border ${toneCls}`}>
                    {label} · {list.length}
                  </div>
                  <div className="border-l border-r border-b border-zinc-200 divide-y divide-zinc-100">
                    {list.map((i) => {
                      const c = cust?.(i.customer_id) || {};
                      const isOverdueInv = i.days_overdue > 0 && i.balance > 0.01;
                      const isSel = selected.has(i.id);
                      return (
                        <div key={i.id} className={`flex items-center justify-between px-2 py-1.5 text-xs ${isSel ? "bg-orange-50" : ""}`}>
                          <input type="checkbox" checked={isSel}
                            onChange={() => toggleSel(i.id)}
                            data-testid={`fu-sel-${i.id}`}
                            className="mr-2 shrink-0 accent-orange-600 cursor-pointer" />
                          <div className="min-w-0 flex-1">
                            <div className="font-semibold text-zinc-900 truncate">
                              {i.invoice_no} · <span className="font-normal">{c.name}</span>
                              {isOverdueInv && <span className="ml-1 text-[9px] font-mono uppercase bg-red-100 text-red-700 px-1">{i.days_overdue}d over</span>}
                            </div>
                            <div className="text-[10px] font-mono text-zinc-500 truncate">
                              📌 {i.follow_up || "Follow up"}{i.follow_up_date ? ` · ${i.follow_up_date}` : ""}
                              {i.balance > 0.01 && <> · <b className="text-orange-700">₹ {fmt(i.balance)}</b> due</>}
                            </div>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            {(c.phone || "").trim() && i.balance > 0.01 && (
                              <button data-testid={`fu-wa-${i.id}`} onClick={() => onWa?.(i)}
                                className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-0.5 inline-flex items-center gap-1">
                                <MessageCircle size={10} /> WA
                              </button>
                            )}
                            <button data-testid={`fu-edit-${i.id}`} onClick={() => onFollowUp?.(i)}
                              className="text-[10px] font-mono uppercase border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5">Reschedule</button>
                            <button data-testid={`fu-record-${i.id}`} onClick={() => onOpenInvoice?.(i)}
                              className="text-[10px] font-mono uppercase border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-0.5">Record</button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
            {orgView === "party" && (
              <div data-testid="fu-party-view" className="space-y-2">
                {byParty.length === 0 ? (
                  <div className="text-center text-zinc-400 text-sm py-10">No parties with pending follow-ups.</div>
                ) : byParty.map((row) => {
                  const bal = row.invoices.reduce((s, i) => s + (i.balance || 0), 0);
                  return (
                    <div key={row.party} data-testid={`fu-party-${row.party}`} className="border border-zinc-200">
                      <div className="bg-zinc-100 px-2 py-1 text-[11px] font-mono flex items-center justify-between">
                        <div className="font-semibold text-zinc-900 truncate">
                          {row.party}
                          {row.phone && <span className="ml-2 text-[10px] text-zinc-500 font-normal">📞 {row.phone}</span>}
                        </div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-600 whitespace-nowrap">
                          {row.invoices.length} inv · <b className="text-orange-800">₹ {fmt(bal)}</b>
                        </div>
                      </div>
                      <div className="divide-y divide-zinc-100">
                        {row.invoices.map((i) => {
                          const isSel = selected.has(i.id);
                          const isOver = i.days_overdue > 0 && i.balance > 0.01;
                          return (
                            <div key={i.id} className={`flex items-center justify-between px-2 py-1.5 text-xs ${isSel ? "bg-orange-50" : ""}`}>
                              <input type="checkbox" checked={isSel} onChange={() => toggleSel(i.id)}
                                className="mr-2 shrink-0 accent-orange-600 cursor-pointer" />
                              <div className="min-w-0 flex-1">
                                <div className="font-semibold text-zinc-900 truncate">
                                  {i.invoice_no}
                                  {isOver && <span className="ml-1 text-[9px] font-mono uppercase bg-red-100 text-red-700 px-1">{i.days_overdue}d over</span>}
                                </div>
                                <div className="text-[10px] font-mono text-zinc-500 truncate">
                                  📌 {i.follow_up || "Follow up"}{i.follow_up_date ? ` · ${i.follow_up_date}` : " · no date"}
                                  {i.balance > 0.01 && <> · <b className="text-orange-700">₹ {fmt(i.balance)}</b> due</>}
                                </div>
                              </div>
                              <div className="flex gap-1 shrink-0">
                                {(row.phone || "").trim() && i.balance > 0.01 && (
                                  <button onClick={() => onWa?.(i)} className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-0.5 inline-flex items-center gap-1">
                                    <MessageCircle size={10} /> WA
                                  </button>
                                )}
                                <button onClick={() => onFollowUp?.(i)} className="text-[10px] font-mono uppercase border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5">Edit</button>
                                <button onClick={() => onOpenInvoice?.(i)} className="text-[10px] font-mono uppercase border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-0.5">Record</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {totalFollowUps === 0 && (
              <div className="text-center text-zinc-500 text-sm py-10 space-y-2">
                <div>Nothing scheduled yet.</div>
                <div className="text-[11px] font-mono text-zinc-400">
                  Click <b className="text-orange-600">+ New follow-up</b> above, or hit the <b className="text-orange-600">TAG</b> button on any invoice in the Invoices tab.
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Party-wise follow-up roll-up — supplementary view, hidden when the
          organiser is already showing the Party view to avoid duplication. */}
      {orgView === "date" && byParty.length > 0 && (
        <div className="bg-white border border-zinc-300" data-testid="followup-by-party">
          <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Follow-up organiser</div>
              <div className="text-sm font-bold">By party — {byParty.length} partie{byParty.length === 1 ? "" : "s"} with pending follow-ups</div>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-zinc-100 text-zinc-600 uppercase text-[9px] font-mono tracking-wider">
                <tr>
                  <th className="text-left px-3 py-2">Party</th>
                  <th className="text-right px-3 py-2">Follow-ups</th>
                  <th className="text-right px-3 py-2">Balance ₹</th>
                  <th className="text-left px-3 py-2">Invoices</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {byParty.map((row) => {
                  const bal = row.invoices.reduce((s, i) => s + (i.balance || 0), 0);
                  return (
                    <tr key={row.party} className="border-b border-zinc-100 hover:bg-zinc-50">
                      <td className="px-3 py-2 font-semibold text-zinc-900 font-sans">{row.party}</td>
                      <td className="px-3 py-2 text-right">{row.invoices.length}</td>
                      <td className="px-3 py-2 text-right text-orange-800 font-semibold">₹ {fmt(bal)}</td>
                      <td className="px-3 py-2">
                        <div className="flex flex-wrap gap-1">
                          {row.invoices.map((i) => (
                            <button key={i.id} onClick={() => onFollowUp?.(i)}
                              className="text-[10px] uppercase border border-zinc-300 bg-white hover:bg-zinc-100 px-1.5 py-0.5"
                              title={i.follow_up || "follow up"}>
                              {i.invoice_no}
                            </button>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {showFuPicker && (
        <FuInvoicePickerModal
          invoices={invoices}
          cust={cust}
          onPick={(inv) => { setShowFuPicker(false); onFollowUp?.(inv); }}
          onClose={() => setShowFuPicker(false)}
        />
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Follow-up invoice picker — used by the Schedule tab "+ New follow-up" flow.
// Just a searchable list of open invoices; picking one delegates to the
// existing FollowUpModal so we reuse tag + date validation.
// -----------------------------------------------------------------------------
function FuInvoicePickerModal({ invoices, cust, onPick, onClose }) {
  const [q, setQ] = useState("");
  const open = useMemo(() => invoices.filter((i) => i.status !== "paid" && i.status !== "void"), [invoices]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return open;
    return open.filter((i) => {
      const c = cust?.(i.customer_id);
      return (i.invoice_no || "").toLowerCase().includes(t) || (c?.name || "").toLowerCase().includes(t);
    });
  }, [q, open, cust]);
  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="bg-white w-full max-w-2xl my-6 border border-zinc-300 flex flex-col max-h-[85vh]"
        data-testid="fu-picker">
        <div className="flex items-center justify-between border-b border-zinc-300 px-5 py-3 bg-zinc-50">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Follow-up organiser</div>
            <h3 className="text-base font-bold">Pick an invoice to tag</h3>
          </div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-3 border-b border-zinc-200">
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search invoice #, party…"
            data-testid="fu-picker-search"
            className="w-full h-9 px-2 border border-zinc-300 text-sm font-mono" />
        </div>
        <div className="overflow-y-auto flex-1 divide-y divide-zinc-100">
          {filtered.length === 0 ? (
            <div className="text-center text-zinc-500 text-xs py-8">No open invoices match.</div>
          ) : filtered.slice(0, 100).map((i) => {
            const c = cust?.(i.customer_id) || {};
            const isOver = i.days_overdue > 0 && i.balance > 0.01;
            return (
              <button key={i.id} data-testid={`fu-pick-${i.id}`} onClick={() => onPick(i)}
                className="w-full text-left px-3 py-2 hover:bg-orange-50">
                <div className="text-sm font-semibold text-zinc-900">
                  {i.invoice_no} · <span className="font-normal">{c.name || "—"}</span>
                  {isOver && <span className="ml-2 text-[9px] font-mono uppercase bg-red-100 text-red-700 px-1">{i.days_overdue}d over</span>}
                </div>
                <div className="text-[10px] font-mono text-zinc-500">
                  Due {i.due_date || "—"} · Balance <b className="text-orange-700">₹ {(i.balance || 0).toLocaleString("en-IN")}</b>
                  {i.follow_up && <> · already tagged: {i.follow_up}</>}
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------------
// Party Focus panel — appears below the invoice grid when a party is selected
// in the "All parties" filter. Dashboard widgets above stay whole-company; this
// panel is the drilldown for the operator with 3 mini-tabs.
// -----------------------------------------------------------------------------
function PartyFocusPanel({ customer, invoices, cust, health, initialTab, initialTabTick, onOpenInvoice, onFollowUp, onWa, onEditParty, onReloadDashboard, companyFilter }) {
  const [tab, setTab] = useState(initialTab || "summary"); // "summary" | "followups" | "payments" | "reminders"
  const [statement, setStatement] = useState(null);
  const [advance, setAdvance] = useState(null);
  const [payments, setPayments] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  // Row-level payment actions — Edit (all fields) or Move (change invoice).
  const [editingPayment, setEditingPayment] = useState(null);
  const [movingPayment, setMovingPayment] = useState(null);
  // bump to force a reload after a new call log is added
  const [refreshTick, setRefreshTick] = useState(0);

  // Honour external requests to jump to a specific tab (e.g. clicking a
  // temperature chip from the aging table opens the reminders tab).
  useEffect(() => {
    if (initialTab) setTab(initialTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTabTick]);

  useEffect(() => {
    if (!customer.id) return;
    (async () => {
      setBusy(true);
      try {
        const [st, adv, cl] = await Promise.all([
          api.get(`/customers/${customer.id}/statement${companyFilter ? `?company_id=${encodeURIComponent(companyFilter)}` : ""}`),
          api.get(`/customers/${customer.id}/advance${companyFilter ? `?company_id=${encodeURIComponent(companyFilter)}` : ""}`),
          api.get(`/customers/${customer.id}/call-logs`),
        ]);
        setStatement(st.data); setAdvance(adv.data); setCallLogs(cl.data || []);
        // Payments: gather per-invoice for the party. Batching keeps the
        // request count bounded to the invoices we already have in scope.
        const results = await Promise.all(
          invoices.map((i) => api.get(`/invoices/${i.id}/payments`).then((r) => r.data.map((p) => ({ ...p, invoice_no: i.invoice_no }))))
        );
        setPayments(results.flat().sort((a, b) => (b.date || "").localeCompare(a.date || "")));
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
      finally { setBusy(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id, invoices.length, refreshTick, companyFilter]);

  const logCall = async () => {
    const outcome = window.prompt("Outcome? (spoke / no_answer / callback / promise / refused)", "spoke");
    if (!outcome) return;
    let promise_date = ""; let promise_amount = 0;
    if (outcome === "promise") {
      promise_date = window.prompt("Promise-to-pay date (YYYY-MM-DD)?", "") || "";
      const suggestedBal = (statement?.totals?.balance || 0).toFixed(0);
      promise_amount = parseFloat(window.prompt("Promise amount ₹?", suggestedBal) || "0") || 0;
    }
    const notes = window.prompt("Notes?", "") || "";
    try {
      await api.post("/call-logs", {
        customer_id: customer.id,
        invoice_id: (invoices[0] || {}).id || "",
        date: new Date().toISOString().slice(0, 10),
        channel: "call", outcome, promise_date, promise_amount, notes,
      });
      toast.success("Call logged" + (promise_date ? ` · promise ${promise_date}` : ""));
      setRefreshTick((n) => n + 1);
      setTab("reminders");
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  // Re-run the FIFO cascade across every payment from a given date onwards
  // — used after an opening balance is added mid-year or an invoice is
  // back-dated. Destructive: rewrites payment allocations. Guarded by an
  // explicit confirm prompt.
  const reconcileLedger = async () => {
    if (reconciling) return;
    const defaultFrom = "2026-04-01"; // FY 25-26 start (Indian financial year)
    const from = window.prompt(
      "Reconcile all payments FROM which date onwards?\n\n" +
      "This will re-apply every payment (in date order) starting oldest-invoice-first — " +
      "so the opening balance and older dues get squared off before newer invoices.",
      defaultFrom,
    );
    if (!from) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) {
      return toast.error("Date must be YYYY-MM-DD");
    }
    if (!window.confirm(`Re-apply every payment on/after ${from} in strict FIFO order?\n\nExisting payment records will be rewritten (dates + amounts + refs preserved).`)) return;
    setReconciling(true);
    try {
      const r = await api.post(`/customers/${customer.id}/reconcile`, {
        from_date: from,
        ...(companyFilter ? { company_id: companyFilter } : {}),
      });
      const d = r.data || {};
      if (d.status === "noop") {
        toast.info(d.message || "Nothing to reconcile");
      } else {
        toast.success(`Reconciled ${d.payments_reconciled || 0} payment(s)`
          + (d.advance_created > 0 ? ` · advance created ₹${d.advance_created.toLocaleString("en-IN")}` : ""));
        setRefreshTick((n) => n + 1);
        // Also refresh the parent's invoice grid / KPIs / aging so the
        // dashboard stays in sync with the DB post-reconcile.
        onReloadDashboard?.();
      }
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setReconciling(false); }
  };

  const fmt = (n) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const totals = statement?.totals || {};
  const followUps = invoices.filter(
    (i) => i.status !== "paid" && i.status !== "void" && ((i.follow_up || "").trim() || (i.follow_up_date || "").trim()),
  );

  return (
    <div className="mt-4 bg-white border-2 border-blue-500" data-testid="party-focus">
      <div className="px-4 py-2 border-b border-blue-500 bg-blue-50 flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-700">Party focus · not affecting the dashboard above</div>
          <h3 className="text-base font-bold text-blue-900 flex items-center gap-2 flex-wrap">
            <span>{customer.name}
              {customer.company && <span className="text-zinc-500 font-normal"> · {customer.company}</span>}
            </span>
            {health && (
              <>
                <PartyHealthChip
                  health={health}
                  size="lg"
                  onClick={() => setTab("reminders")}
                  testid={`focus-health-${customer.id}`}
                />
                <HealthTrendSparkline trend={health.trend} temperature={health.temperature} />
              </>
            )}
          </h3>
          {customer.phone && <div className="text-[11px] font-mono text-zinc-600">📞 {customer.phone}</div>}
          {health && (
            <div className="text-[10px] font-mono text-zinc-500 mt-0.5">{health.reason}</div>
          )}
        </div>
        <a href={`/customers/${customer.id}/statement${companyFilter ? `?firm=${encodeURIComponent(companyFilter)}` : ""}`} target="_blank" rel="noreferrer"
          data-testid="focus-open-statement"
          className="text-[10px] uppercase font-mono border border-blue-500 text-blue-700 hover:bg-blue-100 px-3 py-1 inline-flex items-center gap-1">
          <FileText size={12} /> Full statement
        </a>
        {onEditParty && (
          <button onClick={() => onEditParty(customer)} data-testid="focus-edit-party"
            className="text-[10px] uppercase font-mono border border-orange-500 text-orange-700 hover:bg-orange-100 px-3 py-1 inline-flex items-center gap-1">
            <Pencil size={12} /> Edit party
          </button>
        )}
        <button onClick={reconcileLedger} data-testid="focus-reconcile"
          disabled={reconciling}
          title="Re-apply every payment from a chosen date onwards in strict FIFO order (oldest invoice / opening balance first)"
          className={`text-[10px] uppercase font-mono border border-purple-500 text-purple-700 px-3 py-1 inline-flex items-center gap-1 ${reconciling ? "opacity-50 cursor-wait" : "hover:bg-purple-100"}`}>
          <RefreshCw size={12} className={reconciling ? "animate-spin" : ""} /> {reconciling ? "Reconciling…" : "Reconcile"}
        </button>
        <button onClick={async () => {
          if (!companyFilter) return toast.error("Pick a Firm above to set opening balance");
          const amt = window.prompt(`Opening balance ₹ for this party in the SELECTED FIRM only?\n(0 to clear)`, "0");
          if (amt === null) return;
          const date = window.prompt("As-of date (YYYY-MM-DD)?", "2026-03-31");
          if (!date) return;
          try {
            await api.put(`/billing-companies/${companyFilter}/openings/${customer.id}`, {
              amount: parseFloat(amt) || 0, date,
            });
            toast.success("Opening balance updated for this firm");
            setRefreshTick((n) => n + 1);
            onReloadDashboard?.();
          } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
        }} data-testid="focus-set-ob"
          title="Set THIS PARTY's opening balance for the selected firm only. Requires a firm to be picked in the header above."
          className="text-[10px] uppercase font-mono border border-emerald-500 text-emerald-700 hover:bg-emerald-100 px-3 py-1 inline-flex items-center gap-1">
          <CircleDollarSign size={12} /> Set OB
        </button>
        <button onClick={logCall} data-testid="focus-log-call"
          className="text-[10px] uppercase font-mono border border-zinc-700 bg-zinc-900 text-white hover:bg-black px-3 py-1 inline-flex items-center gap-1">
          <Phone size={12} /> + Log call
        </button>
      </div>

      <div className="flex gap-1 border-b border-blue-200 bg-blue-50/40 px-2">
        {[
          { k: "summary",   label: `Summary` },
          { k: "followups", label: `Follow-ups (${followUps.length})` },
          { k: "payments",  label: `Payments (${payments.length})` },
          { k: "reminders", label: `Reminders (${callLogs.length})` },
        ].map(({ k, label }) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`focus-tab-${k}`}
            className={`px-3 py-1.5 text-[11px] uppercase tracking-wider font-mono border-b-2 -mb-px ${
              tab === k ? "border-blue-600 text-blue-900" : "border-transparent text-zinc-500 hover:text-zinc-800"
            }`}>{label}</button>
        ))}
      </div>

      <div className="p-3">
        {busy && <div className="py-6 text-center text-xs text-zinc-500">Loading party details…</div>}

        {!busy && tab === "summary" && statement && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="focus-summary">
            <FocusStat label="Invoiced"       value={`₹ ${fmt(totals.invoiced)}`} tone="zinc" />
            <FocusStat label="Paid"           value={`₹ ${fmt(totals.paid)}`} tone="emerald" />
            <FocusStat label="Balance Due"    value={`₹ ${fmt(totals.balance)}`} tone="orange" />
            <FocusStat label="Overdue"        value={`₹ ${fmt(totals.overdue)}`} tone={totals.overdue > 0 ? "red" : "zinc"}
              note={totals.oldest_overdue_days > 0 ? `oldest ${totals.oldest_overdue_days}d` : ""} />
            <FocusStat label="Advance"        value={`₹ ${fmt(totals.advance_balance || 0)}`} tone={(totals.advance_balance || 0) > 0 ? "blue" : "zinc"}
              note={(totals.advance_balance || 0) > 0 ? "auto-applies next invoice" : ""} />
          </div>
        )}

        {!busy && tab === "followups" && (
          <div data-testid="focus-followups" className="space-y-1">
            {followUps.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-6">No pending follow-ups for this party.</div>
            ) : followUps.map((i) => {
              const isOver = i.days_overdue > 0 && i.balance > 0.01;
              return (
                <div key={i.id} className="flex items-center justify-between border-b border-zinc-100 py-1.5 text-xs">
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-zinc-900 truncate">
                      {i.invoice_no}
                      {isOver && <span className="ml-1 text-[9px] uppercase bg-red-100 text-red-700 px-1">{i.days_overdue}d over</span>}
                    </div>
                    <div className="text-[10px] font-mono text-zinc-500 truncate">
                      📌 {i.follow_up || "Follow up"}{i.follow_up_date ? ` · ${i.follow_up_date}` : ""}
                      {i.balance > 0.01 && <> · <b className="text-orange-700">₹ {fmt(i.balance)}</b> due</>}
                    </div>
                  </div>
                  <div className="flex gap-1 shrink-0">
                    {(customer.phone || "").trim() && i.balance > 0.01 && (
                      <button onClick={() => onWa?.(i)} className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-0.5 inline-flex items-center gap-1">
                        <MessageCircle size={10} /> WA
                      </button>
                    )}
                    <button onClick={() => onFollowUp?.(i)} className="text-[10px] font-mono uppercase border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5">Reschedule</button>
                    <button onClick={() => onOpenInvoice?.(i)} className="text-[10px] font-mono uppercase border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-0.5">Record</button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!busy && tab === "payments" && (
          <div data-testid="focus-payments" className="max-h-72 overflow-y-auto">
            {payments.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-6">No payments recorded yet for this party.</div>
            ) : (
              <table className="w-full text-xs">
                <thead className="bg-zinc-100 text-zinc-600 uppercase text-[9px] font-mono tracking-wider sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1.5">Date</th>
                    <th className="text-left px-2 py-1.5">Invoice</th>
                    <th className="text-left px-2 py-1.5">Mode</th>
                    <th className="text-left px-2 py-1.5">Ref</th>
                    <th className="text-right px-2 py-1.5">Amount ₹</th>
                    <th className="text-left px-2 py-1.5">Source</th>
                    <th className="text-right px-2 py-1.5">Actions</th>
                  </tr>
                </thead>
                <tbody className="font-mono">
                  {payments.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`focus-payment-${p.id}`}>
                      <td className="px-2 py-1.5">{p.date}</td>
                      <td className="px-2 py-1.5 font-semibold">{p.invoice_no}</td>
                      <td className="px-2 py-1.5">{p.mode}</td>
                      <td className="px-2 py-1.5 text-zinc-500">{p.ref_no || "—"}</td>
                      <td className="px-2 py-1.5 text-right text-emerald-700 font-semibold">{fmt(p.amount)}</td>
                      <td className="px-2 py-1.5 text-[10px] uppercase">
                        {p.source === "cascade" && <span className="bg-orange-100 text-orange-800 px-1">cascade</span>}
                        {p.source === "advance_applied" && <span className="bg-blue-100 text-blue-800 px-1">advance</span>}
                        {(!p.source || p.source === "manual") && <span className="text-zinc-500">manual</span>}
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <button onClick={() => setEditingPayment(p)} data-testid={`focus-payment-edit-${p.id}`}
                          className="text-[9px] uppercase border border-zinc-300 hover:border-orange-500 hover:text-orange-700 px-1.5 py-0.5 mr-1">
                          Edit
                        </button>
                        {p.source !== "advance_applied" && p.source !== "cascade" && !p.parent_payment_id && (
                          <button onClick={() => setMovingPayment(p)} data-testid={`focus-payment-move-${p.id}`}
                            className="text-[9px] uppercase border border-zinc-300 hover:border-purple-500 hover:text-purple-700 px-1.5 py-0.5">
                            Move
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
        {editingPayment && (
          <PaymentEditModal payment={editingPayment} onClose={() => setEditingPayment(null)}
            onSaved={() => { setEditingPayment(null); setRefreshTick((n) => n + 1); onReloadDashboard?.(); }} />
        )}
        {movingPayment && (
          <PaymentMoveModal payment={movingPayment} invoices={invoices} onClose={() => setMovingPayment(null)}
            onSaved={() => { setMovingPayment(null); setRefreshTick((n) => n + 1); onReloadDashboard?.(); }} />
        )}

        {!busy && tab === "reminders" && (
          <div data-testid="focus-reminders" className="max-h-80 overflow-y-auto">
            {callLogs.length === 0 ? (
              <div className="text-center text-xs text-zinc-500 py-6">
                No reminders yet. Send a WhatsApp reminder or click <b>+ Log call</b> above to start the timeline.
              </div>
            ) : (
              <div className="relative pl-4">
                <div className="absolute left-1 top-1 bottom-1 w-px bg-zinc-200"></div>
                {callLogs.map((l) => {
                  const isWa = l.channel === "whatsapp";
                  const isPromise = l.outcome === "promise";
                  const isRefused = l.outcome === "refused";
                  const isMiss = ["no_answer", "wrong_number"].includes(l.outcome);
                  const dot = isWa ? "bg-green-500 border-green-600"
                    : (isPromise ? "bg-blue-500 border-blue-600"
                    : (isRefused ? "bg-red-500 border-red-600"
                    : (isMiss ? "bg-zinc-400 border-zinc-500" : "bg-orange-500 border-orange-600")));
                  const badge = isWa
                    ? (l.outcome === "wa_broadcast" ? "WA broadcast" : "WA sent")
                    : l.outcome.replace(/_/g, " ");
                  const badgeCls = isWa
                    ? "bg-green-100 text-green-800 border-green-300"
                    : (isPromise ? "bg-blue-100 text-blue-800 border-blue-300"
                    : (isRefused ? "bg-red-100 text-red-800 border-red-300"
                    : (isMiss ? "bg-zinc-100 text-zinc-700 border-zinc-300" : "bg-orange-100 text-orange-800 border-orange-300")));
                  return (
                    <div key={l.id} className="relative flex items-start gap-3 py-2 border-b border-zinc-100 last:border-b-0"
                         data-testid={`focus-reminder-${l.id}`}>
                      <div className={`absolute -left-3 top-3 w-2.5 h-2.5 rounded-full border-2 ${dot}`}></div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-[10px] font-mono text-zinc-500">{l.date}</span>
                          <span className={`text-[9px] font-mono uppercase tracking-wider border px-1 ${badgeCls}`}>
                            {isWa ? <MessageCircle size={9} className="inline mr-0.5" /> : <Phone size={9} className="inline mr-0.5" />}
                            {badge}
                          </span>
                          {isPromise && l.promise_date && (
                            <span className="text-[9px] font-mono uppercase tracking-wider border border-blue-400 bg-blue-50 text-blue-800 px-1">
                              → pay ₹{(l.promise_amount || 0).toLocaleString("en-IN")} by {l.promise_date}
                            </span>
                          )}
                        </div>
                        {l.notes && <div className="text-xs text-zinc-700 mt-0.5 font-sans">{l.notes}</div>}
                        {l.by && <div className="text-[9px] font-mono text-zinc-400 mt-0.5">by {l.by}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function FocusStat({ label, value, tone = "zinc", note }) {
  const cls = {
    orange: "border-orange-500 bg-orange-50 text-orange-900",
    red: "border-red-500 bg-red-50 text-red-900",
    emerald: "border-emerald-500 bg-emerald-50 text-emerald-900",
    blue: "border-blue-500 bg-blue-50 text-blue-900",
    zinc: "border-zinc-300 bg-zinc-50 text-zinc-800",
  }[tone];
  return (
    <div className={`border-2 ${cls} p-2 text-center`}>
      <div className="text-[9px] font-mono uppercase tracking-widest opacity-70">{label}</div>
      <div className="text-lg font-bold">{value}</div>
      {note && <div className="text-[9px] font-mono opacity-70">{note}</div>}
    </div>
  );
}

// Payment edit modal — lets ops fix a mis-keyed date / amount / mode / ref /
// note or delete a booked receipt entirely. Amount is server-validated
// against the invoice's remaining ceiling so an increase can never take
// the invoice balance negative.
function PaymentEditModal({ payment, onClose, onSaved }) {
  const [s, setS] = useState({
    date: payment.date || "",
    amount: payment.amount || 0,
    mode: payment.mode || "bank",
    ref_no: payment.ref_no || "",
    note: payment.note || "",
  });
  const [busy, setBusy] = useState(false);
  const cls = "w-full border border-zinc-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-orange-500";
  const save = async () => {
    if (!s.date) return toast.error("Date is required");
    if (!s.amount || s.amount <= 0) return toast.error("Amount must be > 0");
    setBusy(true);
    try {
      await api.put(`/invoices/${payment.invoice_id}/payments/${payment.id}`, s);
      toast.success("Payment updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete this ₹${payment.amount.toLocaleString("en-IN")} payment dated ${payment.date}?`)) return;
    setBusy(true);
    try {
      await api.delete(`/invoices/${payment.invoice_id}/payments/${payment.id}`);
      toast.success("Payment deleted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="payment-edit-modal">
      <div className="bg-white w-96 border-4 border-orange-500">
        <div className="px-4 py-2 bg-orange-500 text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest opacity-80">Edit payment</div>
            <div className="text-sm font-bold">Invoice {payment.invoice_no || "—"}</div>
          </div>
          <button onClick={onClose} className="text-white hover:text-orange-100" data-testid="payment-edit-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Date *</div>
              <input type="date" value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} className={cls} data-testid="payment-edit-date" />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Amount ₹ *</div>
              <input type="number" step="0.01" value={s.amount} onChange={(e) => setS({ ...s, amount: parseFloat(e.target.value) || 0 })} className={cls} data-testid="payment-edit-amount" />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Mode</div>
              <select value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value })} className={cls} data-testid="payment-edit-mode">
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="rtgs">RTGS</option>
                <option value="neft">NEFT</option>
              </select>
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Ref no</div>
              <input value={s.ref_no} onChange={(e) => setS({ ...s, ref_no: e.target.value })} className={cls} data-testid="payment-edit-ref" />
            </label>
          </div>
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Note</div>
            <textarea rows={2} value={s.note} onChange={(e) => setS({ ...s, note: e.target.value })} className={cls} data-testid="payment-edit-note" />
          </label>
          {payment.source === "cascade" && (
            <div className="text-[10px] font-mono text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1">
              This is a cascade-child payment. Editing here only touches this row — the parent payment's total stays as-is.
            </div>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
            <button onClick={del} disabled={busy} data-testid="payment-edit-delete"
              className="text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-100 px-3 py-1 inline-flex items-center gap-1 disabled:opacity-50">
              <Trash2 size={12} /> Delete
            </button>
            <div className="flex gap-2">
              <button onClick={onClose} className="text-[10px] uppercase font-mono border border-zinc-300 hover:bg-zinc-100 px-3 py-1">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="payment-edit-save"
                className="text-[10px] uppercase font-mono bg-orange-500 text-white hover:bg-orange-600 px-3 py-1 disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Payment move modal — reassigns a payment to a DIFFERENT invoice for the
// same party (e.g. mis-clicked or bank narration ambiguity). The backend
// enforces same-customer + ceiling on the destination.
function PaymentMoveModal({ payment, invoices, onClose, onSaved }) {
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const options = (invoices || [])
    .filter((i) => i.id !== payment.invoice_id && (i.status !== "void"))
    .sort((a, b) => (a.invoice_date || "").localeCompare(b.invoice_date || ""));
  const save = async () => {
    if (!target) return toast.error("Pick the destination invoice");
    setBusy(true);
    try {
      await api.patch(`/payments/${payment.id}/reassign`, { new_invoice_id: target });
      toast.success("Payment moved");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="payment-move-modal">
      <div className="bg-white w-[26rem] border-4 border-purple-500">
        <div className="px-4 py-2 bg-purple-500 text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest opacity-80">Move payment</div>
            <div className="text-sm font-bold">₹ {payment.amount.toLocaleString("en-IN")} · {payment.date}</div>
          </div>
          <button onClick={onClose} className="text-white hover:text-purple-100" data-testid="payment-move-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="text-[10px] font-mono text-zinc-600">
            Moving from <b className="text-zinc-900">{payment.invoice_no || "—"}</b> to…
          </div>
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Destination invoice *</div>
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="payment-move-target"
              className="w-full border border-zinc-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-purple-500">
              <option value="">— pick an invoice —</option>
              {options.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.invoice_no} · {i.invoice_date} · ₹{(i.gross_amount || 0).toLocaleString("en-IN")}
                </option>
              ))}
            </select>
          </label>
          <div className="text-[10px] font-mono text-purple-700 bg-purple-50 border border-purple-200 px-2 py-1">
            The payment's date, mode, ref and amount are preserved. If the destination invoice can't fit the full amount, the API will refuse.
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button onClick={onClose} className="text-[10px] uppercase font-mono border border-zinc-300 hover:bg-zinc-100 px-3 py-1">Cancel</button>
            <button onClick={save} disabled={busy || !target} data-testid="payment-move-save"
              className="text-[10px] uppercase font-mono bg-purple-500 text-white hover:bg-purple-600 px-3 py-1 disabled:opacity-50">
              {busy ? "Moving…" : "Move payment"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Typeable party filter — replaces the native <select> so operators with
// hundreds of parties can jump straight to a name by typing. Renders as a
// small input with a floating scroll-list of matches; clicking a match sets
// the id and closes. Empty input = "All parties".
function PartyPicker({ customers, value, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = React.useRef(null);
  // Track outside-click to close the dropdown without stealing focus.
  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const selected = customers.find((c) => c.id === value);
  // Show the selected party's name in the input when a value is set AND the
  // user hasn't started typing. Once they type, we treat it as a fresh
  // search query and don't clobber it.
  const displayValue = query || (selected ? selected.name : "");
  const q = query.trim().toLowerCase();
  const matches = q
    ? customers.filter((c) =>
        (c.name || "").toLowerCase().includes(q) ||
        (c.company || "").toLowerCase().includes(q) ||
        (c.phone || "").includes(q)
      ).slice(0, 30)
    : customers.slice(0, 50);
  const pick = (cid) => {
    onChange(cid);
    setQuery("");
    setOpen(false);
  };
  return (
    <div ref={wrapRef} className="relative ml-2" data-testid="filter-customer-wrap">
      <div className="flex items-center gap-1">
        <input
          data-testid="filter-customer"
          type="text"
          value={displayValue}
          placeholder="🔍 Type party name…"
          onFocus={() => setOpen(true)}
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          className="h-7 w-56 px-2 border border-zinc-300 text-xs bg-white font-mono focus:outline-none focus:ring-1 focus:ring-orange-500"
        />
        {value && (
          <button type="button" onClick={() => pick("")}
            data-testid="filter-customer-clear"
            className="h-7 w-7 border border-zinc-300 bg-white text-zinc-500 hover:bg-zinc-100 flex items-center justify-center"
            title="Clear party filter">
            <X size={12} />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-30 top-full mt-1 w-64 max-h-72 overflow-y-auto bg-white border border-zinc-300 shadow-lg"
             data-testid="filter-customer-list">
          <button type="button" onClick={() => pick("")}
            className={`block w-full text-left px-3 py-1.5 text-xs font-mono border-b border-zinc-100 hover:bg-orange-50 ${!value ? "bg-orange-100 text-orange-800" : ""}`}
            data-testid="filter-customer-opt-all">All parties</button>
          {matches.length === 0 ? (
            <div className="px-3 py-3 text-center text-xs text-zinc-500 font-sans">No party matches "{query}"</div>
          ) : matches.map((c) => (
            <button type="button" key={c.id} onClick={() => pick(c.id)}
              data-testid={`filter-customer-opt-${c.id}`}
              className={`block w-full text-left px-3 py-1.5 text-xs font-mono border-b border-zinc-100 hover:bg-orange-50 ${c.id === value ? "bg-orange-100 text-orange-800" : ""}`}>
              <div className="font-semibold">{c.name}</div>
              {(c.company || c.phone) && <div className="text-[10px] text-zinc-500">{c.company}{c.company && c.phone ? " · " : ""}{c.phone}</div>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Communication-temperature chip — thin pill on the aging table + Today's
// Task rows, big variant in the Party Focus header. Colour + emoji encode
// the temperature; hover tooltip shows the diagnostic reason line.
export function PartyHealthChip({ health, size = "sm", onClick, testid }) {
  if (!health) return null;
  const tone = {
    hot:  "border-red-500 bg-red-50 text-red-800 hover:bg-red-100",
    warm: "border-amber-500 bg-amber-50 text-amber-800 hover:bg-amber-100",
    cold: "border-sky-500 bg-sky-50 text-sky-800 hover:bg-sky-100",
  }[health.temperature] || "border-zinc-300 bg-zinc-50 text-zinc-600";
  const label = { hot: "HOT", warm: "WARM", cold: "COLD" }[health.temperature] || "—";
  const emoji = { hot: "🔥", warm: "🟡", cold: "🥶" }[health.temperature] || "•";
  const isBig = size === "lg";
  const pad = isBig ? "px-2.5 py-1 text-[11px]" : "px-1.5 py-0.5 text-[9px]";
  const cursor = onClick ? "cursor-pointer" : "cursor-default";
  const Tag = onClick ? "button" : "span";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      title={health.reason || ""}
      data-testid={testid || `health-chip-${health.customer_id}`}
      className={`inline-flex items-center gap-1 font-mono uppercase tracking-wider border ${tone} ${pad} ${cursor}`}
    >
      <span>{emoji}</span>
      <span className="font-bold">{label}</span>
      {isBig && <span className="opacity-70">· {Math.round(health.score)}</span>}
    </Tag>
  );
}

// 14-day score sparkline — a compact SVG polyline showing the party's
// communication-temperature trajectory. Drawn next to the big Party Focus
// chip so users can see at a glance whether the party is warming up or
// cooling down. Empty (flat-zero) trends render a subtle placeholder so the
// layout doesn't jump on cold parties with no history.
export function HealthTrendSparkline({ trend, temperature }) {
  if (!Array.isArray(trend) || trend.length < 2) return null;
  const w = 84, h = 22, pad = 2;
  const nz = trend.some((v) => v > 0);
  const stroke = { hot: "#dc2626", warm: "#d97706", cold: "#0284c7" }[temperature] || "#71717a";
  const fill = { hot: "#fecaca", warm: "#fde68a", cold: "#bae6fd" }[temperature] || "#e4e4e7";
  const minV = 0, maxV = 100;
  const xs = trend.map((_, i) => pad + (i * (w - 2 * pad)) / (trend.length - 1));
  const ys = trend.map((v) => h - pad - ((Math.max(minV, Math.min(maxV, v)) - minV) * (h - 2 * pad)) / (maxV - minV));
  const points = xs.map((x, i) => `${x.toFixed(1)},${ys[i].toFixed(1)}`).join(" ");
  const area = `M ${xs[0].toFixed(1)},${h - pad} L ${points.split(" ").join(" L ")} L ${xs[xs.length - 1].toFixed(1)},${h - pad} Z`;
  const first = trend[0] ?? 0, last = trend[trend.length - 1] ?? 0;
  const delta = Math.round(last - first);
  const arrow = delta > 3 ? "↑" : delta < -3 ? "↓" : "→";
  return (
    <span className="inline-flex items-center gap-1" title={`14-day trend · ${delta >= 0 ? "+" : ""}${delta} pts`} data-testid="health-sparkline">
      <svg width={w} height={h} className="align-middle">
        {nz && <path d={area} fill={fill} opacity="0.5" />}
        <polyline
          points={points}
          fill="none"
          stroke={stroke}
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {/* Emphasize the last 3 days with a dot so recent momentum stands out */}
        {trend.slice(-3).map((v, i) => {
          const idx = trend.length - 3 + i;
          return (
            <circle key={i} cx={xs[idx]} cy={ys[idx]} r={i === 2 ? 2 : 1.4} fill={stroke} />
          );
        })}
      </svg>
      <span className="text-[9px] font-mono opacity-70">{arrow} {delta > 0 ? "+" : ""}{delta}</span>
    </span>
  );
}


// -----------------------------------------------------------------------------
// Forecast tab — dedicated cash-inflow forecast. Extracted from ScheduleTab
// per user request. The overdue block below shows *every* past-due invoice
// (no cap) since the operator wants full visibility of what isn't inside the
// N-day window.
// -----------------------------------------------------------------------------
function ForecastTab({ companyFilter }) {
  const [days, setDays] = useState(30);
  const [customDays, setCustomDays] = useState("");
  const [inflow, setInflow] = useState(null);
  const [busy, setBusy] = useState(false);
  const [groupBy, setGroupBy] = useState("day");
  const [forecastMode, setForecastMode] = useState("timely");
  const [overdueSort, setOverdueSort] = useState("oldest");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const params = { days };
        if (companyFilter) params.company_id = companyFilter;
        if (forecastMode === "adjusted") params.adjusted = true;
        const { data } = await api.get("/reports/payments/inflow", { params });
        setInflow(data);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
      finally { setBusy(false); }
    })();
  }, [days, companyFilter, forecastMode]);

  const applyCustomDays = () => {
    const n = parseInt(customDays, 10);
    if (!n || n <= 0 || n > 366) return toast.error("Enter days between 1 and 366");
    setDays(n);
  };
  const fmt = (n) => (Math.round(n) || 0).toLocaleString("en-IN");

  const inflowGroups = useMemo(() => {
    if (!inflow) return [];
    const rows = inflow.rows.filter((r) => r.in_window);
    const dateOf = (r) => (inflow.adjusted ? r.expected_date : r.due_date);
    if (groupBy === "day") {
      const g = {};
      rows.forEach((r) => {
        const k = dateOf(r);
        if (!g[k]) g[k] = { key: k, label: k, invoices: [], total: 0 };
        g[k].invoices.push(r); g[k].total += r.balance;
      });
      return Object.values(g).sort((a, b) => a.key.localeCompare(b.key));
    }
    const monday = (dateStr) => {
      const d = new Date(dateStr);
      const dow = (d.getDay() + 6) % 7;
      d.setDate(d.getDate() - dow); d.setHours(0,0,0,0);
      return d.toISOString().slice(0, 10);
    };
    const g = {};
    rows.forEach((r) => {
      const k = monday(dateOf(r));
      if (!g[k]) g[k] = { key: k, label: `Week of ${k}`, invoices: [], total: 0 };
      g[k].invoices.push(r); g[k].total += r.balance;
    });
    return Object.values(g).sort((a, b) => a.key.localeCompare(b.key));
  }, [inflow, groupBy]);

  const overdueRows = useMemo(() => {
    if (!inflow) return [];
    const rows = inflow.rows.filter((r) => r.is_overdue);
    if (overdueSort === "amount") return [...rows].sort((a, b) => (b.balance || 0) - (a.balance || 0));
    return [...rows].sort((a, b) => (b.days_overdue || 0) - (a.days_overdue || 0));
  }, [inflow, overdueSort]);

  return (
    <div className="space-y-4" data-testid="forecast-tab">
      <div className="bg-white border border-zinc-300" data-testid="inflow-forecast">
        <div className="px-4 py-2 border-b border-zinc-200 bg-zinc-50 flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Cash inflow forecast</div>
            <div className="text-sm font-bold">
              Next {days} day{days === 1 ? "" : "s"} · {forecastMode === "adjusted" ? "party-adjusted basis" : "timely-payment basis"}
              <span className="text-zinc-500 font-normal ml-2 text-[11px] font-mono">{companyFilter ? "· firm-scoped" : "· all firms"}</span>
            </div>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {[7, 14, 30, 60, 90].map((n) => (
              <button key={n} data-testid={`inflow-days-${n}`} onClick={() => setDays(n)}
                className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${days === n ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>{n}d</button>
            ))}
            <input type="number" min={1} max={366} value={customDays} onChange={(e) => setCustomDays(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") applyCustomDays(); }}
              placeholder="N" data-testid="inflow-custom"
              className="w-14 h-7 px-1.5 border border-zinc-300 text-xs font-mono text-center" />
            <button data-testid="inflow-apply" onClick={applyCustomDays}
              className="text-[10px] font-mono uppercase px-2 py-0.5 border border-zinc-400 text-zinc-800 hover:bg-zinc-100">Go</button>
          </div>
        </div>

        <div className="px-3 py-1.5 border-b border-zinc-200 flex items-center gap-2 flex-wrap">
          <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Basis</span>
          {[
            { k: "timely",   label: "Timely payment", hint: "Every invoice collected on its due date" },
            { k: "adjusted", label: "Party-adjusted", hint: "Uses each party's avg lateness from past payments" },
          ].map((m) => (
            <button key={m.k} data-testid={`inflow-mode-${m.k}`} title={m.hint}
              onClick={() => setForecastMode(m.k)}
              className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${forecastMode === m.k ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>{m.label}</button>
          ))}
          {forecastMode === "adjusted" && inflow && Object.keys(inflow.party_lateness || {}).length > 0 && (
            <span className="text-[10px] font-mono text-zinc-500">
              (learned from {Object.keys(inflow.party_lateness).length} part{Object.keys(inflow.party_lateness).length === 1 ? "y" : "ies"}' history)
            </span>
          )}
        </div>

        {busy && <div className="p-10 text-center text-xs text-zinc-500">Loading forecast…</div>}

        {inflow && !busy && (
          <>
            <div className="grid grid-cols-3 gap-2 p-3 border-b border-zinc-200">
              <div className="border-2 border-emerald-500 bg-emerald-50 p-3 text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700">Expected in {days}d</div>
                <div className="text-2xl font-bold text-emerald-900">₹ {fmt(inflow.total)}</div>
              </div>
              <div className="border-2 border-red-500 bg-red-50 p-3 text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-red-700">Overdue carry-fwd</div>
                <div className="text-2xl font-bold text-red-900">₹ {fmt(inflow.overdue_carry)}</div>
                <div className="text-[9px] font-mono text-red-700">every past-due invoice below</div>
              </div>
              <div className="border-2 border-zinc-400 bg-zinc-50 p-3 text-center">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-600">Total open</div>
                <div className="text-2xl font-bold text-zinc-900">₹ {fmt(inflow.total + inflow.overdue_carry)}</div>
              </div>
            </div>

            <div className="px-3 py-2 border-b border-zinc-200 flex items-center gap-2">
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Group by</span>
              {[{ k: "day", label: "Day" }, { k: "week", label: "Week" }].map((g) => (
                <button key={g.k} data-testid={`inflow-group-${g.k}`} onClick={() => setGroupBy(g.k)}
                  className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${groupBy === g.k ? "bg-orange-600 text-white border-orange-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>{g.label}</button>
              ))}
            </div>

            <div className="p-3 space-y-3 max-h-[55vh] overflow-y-auto">
              {inflowGroups.length === 0 && (
                <div className="text-center text-zinc-400 text-sm py-10">
                  No invoices due in the next {days} day{days === 1 ? "" : "s"}. Widen the window or check overdue list below.
                </div>
              )}
              {inflowGroups.map((g) => (
                <div key={g.key} data-testid={`inflow-group-${g.key}`}>
                  <div className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 border border-emerald-500 bg-emerald-50 text-emerald-800 flex justify-between">
                    <span>{g.label}</span>
                    <span>{g.invoices.length} inv · <b>₹ {fmt(g.total)}</b></span>
                  </div>
                  <div className="border-l border-r border-b border-zinc-200 divide-y divide-zinc-100">
                    {g.invoices.map((r) => (
                      <div key={r.invoice_id} className="flex items-center justify-between px-2 py-1.5 text-xs">
                        <div className="min-w-0 flex-1">
                          <div className="font-semibold text-zinc-900 truncate">
                            {r.invoice_no} · <span className="font-normal">{r.party}</span>
                          </div>
                          <div className="text-[10px] font-mono text-zinc-500">
                            {inflow.adjusted ? (
                              <>Exp {r.expected_date} ({r.days_to_expected <= 0 ? "today" : `in ${r.days_to_expected}d`})
                                {typeof r.avg_lateness_days === "number" && r.avg_lateness_days !== 0 && (
                                  <span className="ml-1 text-[9px] uppercase tracking-widest text-emerald-700">
                                    {r.avg_lateness_days > 0 ? `+${r.avg_lateness_days}d late avg` : `${r.avg_lateness_days}d early avg`}
                                  </span>
                                )}
                                <span className="text-zinc-400"> · due {r.due_date}</span>
                              </>
                            ) : (
                              <>Due {r.due_date} ({r.days_to_due <= 0 ? "today" : `in ${r.days_to_due}d`})</>
                            )}
                            {" · "}<b className="text-emerald-700">₹ {fmt(r.balance)}</b>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {inflow && !busy && inflow.overdue_carry > 0.01 && (
        <div className="bg-white border-2 border-red-400" data-testid="inflow-overdue">
          <div className="px-4 py-2 border-b border-red-300 bg-red-50 flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-red-700">Overdue payments — every invoice</div>
              <div className="text-sm font-bold text-red-900">
                {overdueRows.length} invoice{overdueRows.length === 1 ? "" : "s"} · Total <b>₹ {fmt(inflow.overdue_carry)}</b>
              </div>
              <div className="text-[10px] font-mono text-red-700">Not counted in the {days}d forecast — chase separately.</div>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Sort by</span>
              {[{ k: "oldest", label: "Oldest first" }, { k: "amount", label: "Largest first" }].map((s) => (
                <button key={s.k} data-testid={`overdue-sort-${s.k}`} onClick={() => setOverdueSort(s.k)}
                  className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${overdueSort === s.k ? "bg-red-600 text-white border-red-600" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>{s.label}</button>
              ))}
            </div>
          </div>
          <div className="max-h-[55vh] overflow-y-auto">
            <table className="w-full text-xs">
              <thead className="bg-red-50 text-red-800 uppercase text-[9px] font-mono tracking-wider sticky top-0">
                <tr>
                  <th className="text-left px-3 py-2 border-b border-red-200">Invoice #</th>
                  <th className="text-left px-3 py-2 border-b border-red-200">Party</th>
                  <th className="text-left px-3 py-2 border-b border-red-200">Company</th>
                  <th className="text-left px-3 py-2 border-b border-red-200">Due Date</th>
                  <th className="text-right px-3 py-2 border-b border-red-200">Overdue</th>
                  <th className="text-right px-3 py-2 border-b border-red-200">Balance ₹</th>
                </tr>
              </thead>
              <tbody className="font-mono">
                {overdueRows.map((r) => (
                  <tr key={r.invoice_id} className="border-b border-zinc-100 hover:bg-red-50/40" data-testid={`overdue-row-${r.invoice_id}`}>
                    <td className="px-3 py-2 font-semibold">{r.invoice_no}</td>
                    <td className="px-3 py-2 font-sans">{r.party}</td>
                    <td className="px-3 py-2 font-sans text-[11px] text-zinc-600">{r.company}</td>
                    <td className="px-3 py-2">{r.due_date}</td>
                    <td className="px-3 py-2 text-right text-red-700 font-semibold">{r.days_overdue}d</td>
                    <td className="px-3 py-2 text-right font-bold text-orange-800">{fmt(r.balance)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot className="sticky bottom-0">
                <tr className="bg-red-100 font-bold border-t-2 border-red-400">
                  <td colSpan={5} className="px-3 py-2 text-right text-red-800">TOTAL OVERDUE</td>
                  <td className="px-3 py-2 text-right text-red-900">{fmt(inflow.overdue_carry)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------------
// Today's Collection Task — a simple daily playbook driven by the backend
// scoring endpoint. Each party is routed to ONE action bucket (WhatsApp /
// Call / Strict / Wait / Advance) with a numeric urgency score and a plain-
// English reason so employees can just work top-to-bottom.
// -----------------------------------------------------------------------------
function TodayTab({ companyFilter, cust, invoices, healthById, onWa, onRecord, onOpenStatement, onOpenParty }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [only, setOnly] = useState("all");

  useEffect(() => {
    (async () => {
      setBusy(true);
      try {
        const params = companyFilter ? { params: { company_id: companyFilter } } : {};
        const { data } = await api.get("/reports/payments/today", params);
        setData(data);
      } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
      finally { setBusy(false); }
    })();
  }, [companyFilter]);

  const fmt = (n) => (Math.round(n) || 0).toLocaleString("en-IN");
  const invById = useMemo(() => Object.fromEntries(invoices.map((i) => [i.id, i])), [invoices]);
  const goWa = (row) => {
    const inv = invById[row.sample_invoice_id];
    if (inv) onWa?.(inv);
    else toast.error("Invoice not loaded — open the Invoices tab first");
  };
  const goRecord = (row) => {
    const inv = invById[row.sample_invoice_id];
    if (inv) onRecord?.(inv);
    else toast.error("Invoice not loaded — open the Invoices tab first");
  };

  const segments = [
    { k: "wa",      label: "WhatsApp today",     Icon: MessageCircle, tone: "green" },
    { k: "call",    label: "Call today",         Icon: Phone,         tone: "orange" },
    { k: "strict",  label: "Strict follow-up",   Icon: AlertTriangle, tone: "red" },
    { k: "wait",    label: "Watch (no action)",  Icon: Clock,         tone: "zinc" },
    { k: "advance", label: "Skip — advance held", Icon: Wallet,       tone: "blue" },
  ];
  const toneCard = { green: "border-green-500", orange: "border-orange-500", red: "border-red-500", zinc: "border-zinc-400", blue: "border-blue-500" };
  const toneHead = { green: "bg-green-600 text-white", orange: "bg-orange-600 text-white", red: "bg-red-600 text-white", zinc: "bg-zinc-500 text-white", blue: "bg-blue-600 text-white" };
  const rowTint  = { green: "bg-green-50/40", orange: "bg-orange-50/40", red: "bg-red-50/40", zinc: "", blue: "bg-blue-50/40" };

  return (
    <div className="space-y-3" data-testid="today-tab">
      <div className="bg-white border border-zinc-300 px-4 py-3">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">Today's collection task</div>
            <h2 className="text-lg font-bold">Who to contact right now — {data?.as_of || ""}</h2>
            <p className="text-[11px] text-zinc-600 mt-1">
              Each party appears in <b>one</b> bucket only. Sorted by urgency (overdue days · balance · past lateness · missed promises). Work top-to-bottom — done for the day.
            </p>
          </div>
          <button data-testid="broadcast-wa"
            onClick={async () => {
              if (!window.confirm("Open a WhatsApp tab for every party in the WhatsApp Today bucket?")) return;
              try {
                const { data: b } = await api.post("/reminders/broadcast", { bucket: "wa" });
                let opened = 0;
                b.rows.forEach((row) => { if (row.wa_link) { window.open(row.wa_link, "_blank", "noreferrer"); opened += 1; } });
                toast.success(`Opened ${opened}/${b.count} chats`);
              } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
            }}
            className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-3 py-1 inline-flex items-center gap-1"
            title="Open every WhatsApp Today party in wa.me — pipe-friendly (30 tabs max)">
            <MessageCircle size={12} /> Broadcast WA bucket
          </button>
        </div>
        <div className="flex flex-wrap gap-1 mt-3">
          <button data-testid="today-only-all" onClick={() => setOnly("all")}
            className={`text-[10px] font-mono uppercase px-2 py-0.5 border ${only === "all" ? "bg-zinc-900 text-white border-zinc-900" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>All</button>
          {segments.map((s) => (
            <button key={s.k} data-testid={`today-only-${s.k}`} onClick={() => setOnly(s.k)}
              className={`text-[10px] font-mono uppercase px-2 py-0.5 border flex items-center gap-1 ${only === s.k ? toneHead[s.tone] + " border-transparent" : "bg-white text-zinc-700 border-zinc-300 hover:bg-zinc-100"}`}>
              <s.Icon size={10} /> {s.label} {data && <span className="opacity-80">({data.counts[s.k]})</span>}
            </button>
          ))}
        </div>
      </div>

      {busy && <div className="p-8 text-center text-xs text-zinc-500">Building today's plan…</div>}

      {data && !busy && segments.map((s) => {
        if (only !== "all" && only !== s.k) return null;
        const rows = data.segments[s.k];
        if (!rows.length && only === "all") return null;
        return (
          <div key={s.k} className={`bg-white border-2 ${toneCard[s.tone]}`} data-testid={`today-seg-${s.k}`}>
            <div className={`px-3 py-1.5 ${toneHead[s.tone]} flex items-center justify-between flex-wrap gap-2`}>
              <div className="flex items-center gap-2">
                <s.Icon size={14} />
                <div className="text-xs font-bold uppercase tracking-widest">{s.label}</div>
              </div>
              <div className="text-[11px] font-mono uppercase tracking-widest">
                {rows.length} part{rows.length === 1 ? "y" : "ies"} · ₹ {fmt(data.totals[s.k])}
              </div>
            </div>
            {rows.length === 0 ? (
              <div className="text-center text-zinc-400 text-xs py-6">Nothing here today. 🎉</div>
            ) : (
              <div className="divide-y divide-zinc-100">
                {rows.map((r, idx) => (
                  <div key={r.customer_id} className={`flex items-center justify-between gap-3 px-3 py-2 ${rowTint[s.tone]}`} data-testid={`today-row-${r.customer_id}`}>
                    <div className="w-8 shrink-0 text-center">
                      <div className="text-[10px] font-mono uppercase text-zinc-400">#{idx + 1}</div>
                      <div className="text-[9px] font-mono text-zinc-500" title="Urgency score">{r.score}</div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-zinc-900 truncate flex items-center gap-2 flex-wrap">
                        <span>{r.party}</span>
                        {(healthById || {})[r.customer_id] && (
                          <PartyHealthChip
                            health={healthById[r.customer_id]}
                            size="sm"
                            onClick={() => onOpenParty?.(r.customer_id)}
                            testid={`today-health-${r.customer_id}`}
                          />
                        )}
                        {r.phone && <span className="text-[10px] font-normal text-zinc-500">📞 {r.phone}</span>}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-600 truncate">
                        {r.invoices} inv · <b className="text-orange-700">₹ {fmt(r.balance)}</b> due · {r.reason}
                      </div>
                    </div>
                    <div className="flex gap-1 shrink-0 flex-wrap justify-end">
                      {s.k === "wa" && r.phone && (
                        <button data-testid={`today-wa-${r.customer_id}`} onClick={() => goWa(r)}
                          className="text-[10px] font-mono uppercase border border-green-600 bg-green-50 text-green-800 hover:bg-green-100 px-2 py-0.5 inline-flex items-center gap-1">
                          <MessageCircle size={10} /> Send WA
                        </button>
                      )}
                      {(s.k === "call" || s.k === "strict") && r.phone && (
                        <a href={`tel:${r.phone.replace(/[^\d+]/g, "")}`}
                          data-testid={`today-call-${r.customer_id}`}
                          className="text-[10px] font-mono uppercase border border-orange-600 bg-orange-50 text-orange-800 hover:bg-orange-100 px-2 py-0.5 inline-flex items-center gap-1">
                          <Phone size={10} /> Call
                        </a>
                      )}
                      <button data-testid={`today-log-${r.customer_id}`}
                        onClick={async () => {
                          const outcome = window.prompt("Outcome? (spoke / no_answer / callback / promise / refused)", "spoke");
                          if (!outcome) return;
                          let promise_date = ""; let promise_amount = 0;
                          if (outcome === "promise") {
                            promise_date = window.prompt("Promise-to-pay date (YYYY-MM-DD)?", "") || "";
                            promise_amount = parseFloat(window.prompt("Promise amount ₹?", String(r.balance)) || "0") || 0;
                          }
                          const notes = window.prompt("Notes?", "") || "";
                          try {
                            await api.post("/call-logs", {
                              customer_id: r.customer_id, invoice_id: r.sample_invoice_id,
                              date: new Date().toISOString().slice(0, 10),
                              outcome, promise_date, promise_amount, notes,
                            });
                            toast.success("Call logged" + (promise_date ? ` · promise ${promise_date}` : ""));
                          } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
                        }}
                        className="text-[10px] font-mono uppercase border border-zinc-500 bg-white text-zinc-800 hover:bg-zinc-100 px-2 py-0.5 inline-flex items-center gap-1"
                        title="Log a call outcome + promise-to-pay date">
                        📝 Log
                      </button>
                      <a href={`/customers/${r.customer_id}/statement${companyFilter ? `?firm=${encodeURIComponent(companyFilter)}` : ""}`} target="_blank" rel="noreferrer"
                        data-testid={`today-stmt-${r.customer_id}`}
                        className="text-[10px] font-mono uppercase border border-zinc-400 text-zinc-700 hover:bg-zinc-100 px-2 py-0.5">STMT</a>
                      {r.sample_invoice_id && (
                        <button data-testid={`today-record-${r.customer_id}`} onClick={() => goRecord(r)}
                          className="text-[10px] font-mono uppercase border border-emerald-600 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 px-2 py-0.5">Record</button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}


// ============================================================
// Receipts tab, Advance registration, Receipt edit modal
// ============================================================
// The Receipts tab shows every real-world receipt as ONE row (base + all
// cascade children + any advance-ledger excess folded in). Users kept
// asking "why does one bank transfer become 3 rows?" — one figure only.

function ReceiptsTab({ companyFilter, receipts, setReceipts, onEdit, onOpenParty }) {
  const [busy, setBusy] = useState(false);
  const [filterCust, setFilterCust] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [mode, setMode] = useState("");
  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (filterCust) params.set("customer_id", filterCust);
      if (companyFilter) params.set("company_id", companyFilter);
      if (from) params.set("from_date", from);
      if (to) params.set("to_date", to);
      if (mode) params.set("mode", mode);
      const r = await api.get(`/payments/receipts?${params.toString()}`);
      setReceipts(r.data.receipts || []);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterCust, from, to, mode, companyFilter]);
  const fmt = (n) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const total = receipts.reduce((s, r) => s + (r.total || 0), 0);
  return (
    <div className="space-y-3">
      <div className="bg-white border border-zinc-300 p-3 flex flex-wrap gap-2 items-center text-xs font-mono">
        <input type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="border border-zinc-300 px-2 py-1" data-testid="receipts-from" />
        <span className="text-zinc-500">→</span>
        <input type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="border border-zinc-300 px-2 py-1" data-testid="receipts-to" />
        <select value={mode} onChange={(e) => setMode(e.target.value)}
          className="border border-zinc-300 px-2 py-1" data-testid="receipts-mode">
          <option value="">All modes</option>
          <option value="bank">Bank</option>
          <option value="cash">Cash</option>
          <option value="upi">UPI</option>
          <option value="cheque">Cheque</option>
          <option value="neft">NEFT</option>
          <option value="rtgs">RTGS</option>
        </select>
        <span className="ml-auto text-zinc-600">
          <b>{receipts.length}</b> receipts · Total <b className="text-emerald-700">₹ {fmt(total)}</b>
        </span>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] tracking-wider">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Party</th>
              <th className="text-left px-3 py-2">Mode</th>
              <th className="text-left px-3 py-2">Ref no</th>
              <th className="text-right px-3 py-2">Amount ₹</th>
              <th className="text-left px-3 py-2">Applied to</th>
              <th className="text-right px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {busy ? (
              <tr><td colSpan={7} className="text-center py-6 text-zinc-500">Loading…</td></tr>
            ) : receipts.length === 0 ? (
              <tr><td colSpan={7} className="text-center py-6 text-zinc-500 font-sans">No receipts in this range.</td></tr>
            ) : receipts.map((r) => {
              const isOrphan = !r.customer_id;
              return (
              <tr key={r.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${isOrphan ? "opacity-60" : ""}`} data-testid={`receipt-row-${r.id}`}>
                <td className="px-3 py-1.5">{r.date}</td>
                <td className="px-3 py-1.5 font-sans">
                  {isOrphan ? (
                    <span className="text-zinc-400 italic">(orphan · linked invoice deleted)</span>
                  ) : (
                    <button onClick={() => onOpenParty?.(r.customer_id)} className="text-zinc-900 hover:text-orange-700 hover:underline text-left">
                      {r.party_name}
                      {r.party_company && <span className="text-zinc-500"> · {r.party_company}</span>}
                    </button>
                  )}
                </td>
                <td className="px-3 py-1.5 uppercase text-[10px]">{r.mode}</td>
                <td className="px-3 py-1.5 text-zinc-600">{r.ref_no || "—"}</td>
                <td className="px-3 py-1.5 text-right text-emerald-700 font-semibold">{fmt(r.total)}</td>
                <td className="px-3 py-1.5 text-[10px] text-zinc-600 max-w-xs">
                  {r.applied.length === 0 ? (
                    <span className="italic">Advance</span>
                  ) : r.applied.length === 1 ? (
                    <span>{r.applied[0].invoice_no || "—"}</span>
                  ) : (
                    <span>{r.applied.length} invoices — {r.applied.map((a) => `${a.invoice_no || "?"} ₹${(a.amount || 0).toLocaleString("en-IN")}`).join(", ")}</span>
                  )}
                  {r.excess > 0 && <span className="text-blue-700"> · advance ₹{r.excess.toLocaleString("en-IN")}</span>}
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button onClick={() => onEdit(r)} data-testid={`receipt-edit-${r.id}`}
                    className="text-[10px] uppercase border border-zinc-300 hover:border-orange-500 hover:text-orange-700 px-2 py-0.5">
                    Edit
                  </button>
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AdvanceRegisterModal({ customers, companies, companyFilter, onClose, onSaved }) {
  const [s, setS] = useState({
    customer_id: "", amount: 0, date: new Date().toISOString().slice(0, 10),
    mode: "bank", ref_no: "", note: "",
    company_id: companyFilter || (companies.length === 1 ? companies[0].id : ""),
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const cls = "w-full border border-zinc-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-emerald-500";
  const save = async () => {
    setErr("");
    if (!s.company_id) return setErr("Pick a firm — advances are booked per firm.");
    if (!s.customer_id) return setErr("Pick a party from the dropdown above.");
    if (!s.amount || s.amount <= 0) return setErr("Amount must be greater than 0.");
    setBusy(true);
    try {
      const r = await api.post(`/customers/${s.customer_id}/advance`, {
        amount: s.amount, date: s.date, mode: s.mode, ref_no: s.ref_no, note: s.note,
        company_id: s.company_id,
      });
      const d = r.data || {};
      if ((d.applied || []).length > 0) {
        toast.success(`Advance ₹${s.amount.toLocaleString("en-IN")} registered · auto-applied to ${d.applied.length} invoice(s)`);
      } else {
        toast.success(`Advance ₹${s.amount.toLocaleString("en-IN")} registered on ledger`);
      }
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="advance-modal">
      <div className="bg-white w-96 border-4 border-emerald-500">
        <div className="px-4 py-2 bg-emerald-500 text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest opacity-80">Register Advance Receipt</div>
            <div className="text-sm font-bold">Money received before an invoice</div>
          </div>
          <button onClick={onClose} className="text-white hover:text-emerald-100" data-testid="advance-close"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Firm *</div>
            <select value={s.company_id} onChange={(e) => setS({ ...s, company_id: e.target.value })}
              className={cls} data-testid="advance-firm">
              <option value="">— pick a firm —</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="text-[9px] text-zinc-500 mt-0.5">Advance is booked to this firm only. It will auto-apply to this firm's oldest open invoice.</div>
          </label>
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Party *</div>
            <select value={s.customer_id} onChange={(e) => setS({ ...s, customer_id: e.target.value })}
              className={cls} data-testid="advance-party">
              <option value="">— pick a party —</option>
              {customers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` · ${c.company}` : ""}</option>)}
            </select>
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Date *</div>
              <input type="date" value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} className={cls} data-testid="advance-date" />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Amount ₹ *</div>
              <input type="number" step="0.01" value={s.amount} onChange={(e) => setS({ ...s, amount: parseFloat(e.target.value) || 0 })} className={cls} data-testid="advance-amount" />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Mode</div>
              <select value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value })} className={cls}>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="rtgs">RTGS</option>
                <option value="neft">NEFT</option>
              </select>
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Ref no</div>
              <input value={s.ref_no} onChange={(e) => setS({ ...s, ref_no: e.target.value })} className={cls} />
            </label>
          </div>
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Note</div>
            <textarea rows={2} value={s.note} onChange={(e) => setS({ ...s, note: e.target.value })} className={cls} />
          </label>
          <div className="text-[10px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-1">
            Will be auto-applied to the party's oldest unpaid invoice. Any leftover stays on the advance ledger.
          </div>
          {err && (
            <div className="text-[11px] font-mono text-red-800 bg-red-50 border border-red-300 px-2 py-1" data-testid="advance-error">
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2 border-t border-zinc-200">
            <button onClick={onClose} className="text-[10px] uppercase font-mono border border-zinc-300 hover:bg-zinc-100 px-3 py-1">Cancel</button>
            <button onClick={save} disabled={busy} data-testid="advance-save"
              className="text-[10px] uppercase font-mono bg-emerald-500 text-white hover:bg-emerald-600 px-3 py-1 disabled:opacity-50">
              {busy ? "Saving…" : "Register"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Receipt edit modal — a receipt is the aggregate of one root Payment +
// its cascade children. Editing here updates the ROOT (date/mode/ref/note).
// Total amount editing is not supported yet — user can delete + re-record.
function ReceiptEditModal({ receipt, onClose, onSaved }) {
  const [s, setS] = useState({
    date: receipt.date || "", mode: receipt.mode || "bank",
    ref_no: receipt.ref_no || "", note: receipt.note || "",
  });
  const [busy, setBusy] = useState(false);
  const cls = "w-full border border-zinc-300 px-2 py-1 text-sm font-mono focus:outline-none focus:ring-1 focus:ring-orange-500";
  const save = async () => {
    if (!s.date) return toast.error("Date required");
    setBusy(true);
    try {
      // Patch the root + each child payment so date / mode / ref stay in
      // sync across all rows that make up this one receipt.
      const primary = receipt.applied[0];
      if (primary) {
        await api.put(`/invoices/${primary.invoice_id}/payments/${primary.payment_id}`, {
          date: s.date, amount: primary.amount, mode: s.mode, ref_no: s.ref_no, note: s.note,
        });
      }
      for (const a of receipt.applied.slice(1)) {
        await api.put(`/invoices/${a.invoice_id}/payments/${a.payment_id}`, {
          date: s.date, amount: a.amount, mode: s.mode, ref_no: s.ref_no, note: s.note,
        });
      }
      toast.success("Receipt updated");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Delete this ₹${receipt.total.toLocaleString("en-IN")} receipt dated ${receipt.date}?\n\nWill unwind all ${receipt.applied.length} invoice application(s).`)) return;
    setBusy(true);
    try {
      for (const a of receipt.applied) {
        await api.delete(`/invoices/${a.invoice_id}/payments/${a.payment_id}`);
      }
      toast.success("Receipt deleted");
      onSaved();
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="receipt-edit-modal">
      <div className="bg-white w-[26rem] border-4 border-orange-500">
        <div className="px-4 py-2 bg-orange-500 text-white flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase font-mono tracking-widest opacity-80">Edit Receipt</div>
            <div className="text-sm font-bold">₹ {receipt.total.toLocaleString("en-IN")} · {receipt.party_name}</div>
          </div>
          <button onClick={onClose} className="text-white hover:text-orange-100"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Date *</div>
              <input type="date" value={s.date} onChange={(e) => setS({ ...s, date: e.target.value })} className={cls} data-testid="receipt-edit-date" />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Mode</div>
              <select value={s.mode} onChange={(e) => setS({ ...s, mode: e.target.value })} className={cls}>
                <option value="bank">Bank</option>
                <option value="cash">Cash</option>
                <option value="upi">UPI</option>
                <option value="cheque">Cheque</option>
                <option value="rtgs">RTGS</option>
                <option value="neft">NEFT</option>
              </select>
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Ref no</div>
              <input value={s.ref_no} onChange={(e) => setS({ ...s, ref_no: e.target.value })} className={cls} />
            </label>
            <label className="text-xs font-mono">
              <div className="text-[10px] uppercase text-zinc-500 mb-1">Applied to</div>
              <div className="text-[10px] text-zinc-500 py-1">{receipt.applied.length} invoice(s)</div>
            </label>
          </div>
          <label className="text-xs font-mono block">
            <div className="text-[10px] uppercase text-zinc-500 mb-1">Note</div>
            <textarea rows={2} value={s.note} onChange={(e) => setS({ ...s, note: e.target.value })} className={cls} />
          </label>
          <div className="text-[10px] font-mono text-orange-700 bg-orange-50 border border-orange-200 px-2 py-1">
            Amount is locked here — to change amount, delete this receipt and re-record via the invoice's WA/Record flow.
          </div>
          <div className="flex items-center justify-between pt-2 border-t border-zinc-200">
            <button onClick={del} disabled={busy} data-testid="receipt-edit-delete"
              className="text-[10px] uppercase font-mono border border-red-500 text-red-700 hover:bg-red-100 px-3 py-1 inline-flex items-center gap-1 disabled:opacity-50">
              <Trash2 size={12} /> Delete
            </button>
            <div className="flex gap-2">
              <button onClick={onClose} className="text-[10px] uppercase font-mono border border-zinc-300 hover:bg-zinc-100 px-3 py-1">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="receipt-edit-save"
                className="text-[10px] uppercase font-mono bg-orange-500 text-white hover:bg-orange-600 px-3 py-1 disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}


// ============================================================
// Openings Tab — per-firm grid to key-in opening balances fast
// ============================================================
// One row per party × per selected firm. Amount and date are editable
// inline. Blur triggers a per-row save (PUT /billing-companies/{co_id}
// /openings/{cid}). Saving 0 clears the OB + removes the synthetic
// invoice for that firm only.
function OpeningsTab({ companies, customers, companyFilter, setCompanyFilter, onSaved }) {
  const [rows, setRows] = useState({});           // cid → {amount, date, dirty, saving, saved}
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [existingByCid, setExistingByCid] = useState({});
  const activeFirm = companyFilter;

  const load = async () => {
    if (!activeFirm) { setRows({}); setExistingByCid({}); return; }
    setBusy(true);
    try {
      const r = await api.get(`/billing-companies/${activeFirm}/openings`);
      const map = {};
      const initial = {};
      for (const row of r.data.rows || []) {
        map[row.customer_id] = row;
        initial[row.customer_id] = { amount: row.amount || 0, date: row.date || "2026-03-31", dirty: false };
      }
      setExistingByCid(map);
      setRows(initial);
    } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeFirm]);

  const setField = (cid, field, value) => {
    setRows((prev) => {
      const next = { ...prev };
      const cur = next[cid] || { amount: 0, date: "2026-03-31" };
      next[cid] = { ...cur, [field]: value, dirty: true, saved: false };
      return next;
    });
  };
  const saveRow = async (cid) => {
    const r = rows[cid];
    if (!r || !r.dirty) return;
    if (!activeFirm) return;
    setRows((prev) => ({ ...prev, [cid]: { ...prev[cid], saving: true } }));
    try {
      await api.put(`/billing-companies/${activeFirm}/openings/${cid}`, {
        amount: parseFloat(r.amount) || 0,
        date: r.date || "2026-03-31",
      });
      setRows((prev) => ({ ...prev, [cid]: { ...prev[cid], saving: false, dirty: false, saved: true } }));
      // clear the saved-flag after a moment for the checkmark UX
      setTimeout(() => setRows((prev) => (prev[cid] ? { ...prev, [cid]: { ...prev[cid], saved: false } } : prev)), 2500);
      onSaved?.();
    } catch (e) {
      setRows((prev) => ({ ...prev, [cid]: { ...prev[cid], saving: false } }));
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const fmt = (n) => (n || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const q = search.trim().toLowerCase();
  const visible = q ? customers.filter((c) =>
    (c.name || "").toLowerCase().includes(q) ||
    (c.company || "").toLowerCase().includes(q) ||
    (c.phone || "").includes(q)
  ) : customers;
  const total = Object.values(rows).reduce((s, r) => s + (parseFloat(r.amount) || 0), 0);
  const activeFirmName = (companies.find((c) => c.id === activeFirm) || {}).name || "—";

  if (!activeFirm) {
    return (
      <div className="bg-white border border-zinc-300 p-6 text-center text-sm text-zinc-600 font-sans"
           data-testid="openings-need-firm">
        <div className="text-orange-600 uppercase font-mono text-[10px] tracking-widest mb-2">Pick a firm first</div>
        Use the <b>Firm</b> filter at the top of the page. Opening balances are stored PER FIRM — each firm has its own opening for the same party.
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="openings-tab">
      <div className="bg-white border border-zinc-300 p-3 flex flex-wrap gap-2 items-center text-xs font-mono">
        <div>
          <span className="text-[10px] uppercase text-zinc-500">Firm ·</span>
          <b className="ml-1 text-zinc-900">{activeFirmName}</b>
        </div>
        <input value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Filter parties…"
          className="ml-auto border border-zinc-300 px-2 py-1 w-56" data-testid="openings-search" />
        <span className="text-zinc-600">
          <b>{Object.values(rows).filter((r) => (parseFloat(r.amount) || 0) > 0).length}</b> openings ·
          Total <b className="text-orange-700 ml-1">₹ {fmt(total)}</b>
        </span>
      </div>
      <div className="bg-white border border-zinc-300 overflow-x-auto max-h-[70vh]">
        <table className="w-full text-xs">
          <thead className="bg-zinc-100 text-zinc-600 uppercase text-[10px] tracking-wider sticky top-0 z-10">
            <tr>
              <th className="text-left px-3 py-2 border-b bg-zinc-100">Party</th>
              <th className="text-left px-3 py-2 border-b bg-zinc-100">Company</th>
              <th className="text-right px-3 py-2 border-b bg-zinc-100">Opening ₹</th>
              <th className="text-left px-3 py-2 border-b bg-zinc-100">As-of date</th>
              <th className="text-right px-3 py-2 border-b bg-zinc-100">Status</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {busy ? (
              <tr><td colSpan={5} className="text-center py-6 text-zinc-500">Loading…</td></tr>
            ) : visible.length === 0 ? (
              <tr><td colSpan={5} className="text-center py-6 text-zinc-500 font-sans">No matching parties.</td></tr>
            ) : visible.map((c) => {
              const r = rows[c.id] || { amount: 0, date: "2026-03-31" };
              return (
                <tr key={c.id} className={`border-b border-zinc-100 hover:bg-zinc-50 ${r.dirty ? "bg-amber-50/60" : ""}`}
                    data-testid={`openings-row-${c.id}`}>
                  <td className="px-3 py-1 font-sans font-semibold">{c.name}</td>
                  <td className="px-3 py-1 text-zinc-600">{c.company || "—"}</td>
                  <td className="px-3 py-1 text-right">
                    <input type="number" step="0.01" value={r.amount}
                      onChange={(e) => setField(c.id, "amount", e.target.value)}
                      onBlur={() => saveRow(c.id)}
                      data-testid={`openings-amount-${c.id}`}
                      className="w-32 text-right border border-zinc-300 px-2 py-0.5 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500" />
                  </td>
                  <td className="px-3 py-1">
                    <input type="date" value={r.date}
                      onChange={(e) => setField(c.id, "date", e.target.value)}
                      onBlur={() => saveRow(c.id)}
                      data-testid={`openings-date-${c.id}`}
                      className="border border-zinc-300 px-2 py-0.5 font-mono focus:outline-none focus:ring-1 focus:ring-orange-500" />
                  </td>
                  <td className="px-3 py-1 text-right text-[10px]">
                    {r.saving && <span className="text-blue-700">Saving…</span>}
                    {r.saved && <span className="text-emerald-700">✓ Saved</span>}
                    {r.dirty && !r.saving && !r.saved && <span className="text-amber-700">● Unsaved</span>}
                    {!r.dirty && !r.saving && !r.saved && (parseFloat(r.amount) || 0) > 0 && <span className="text-zinc-400">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] font-mono text-zinc-500 px-1">
        Amount saves on blur (leave the field). Enter <b>0</b> to clear a party's opening for this firm.
        Openings on other firms are untouched.
      </div>
    </div>
  );
}

